import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useScanMonitor } from '../hooks/useScanMonitor';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose
} from './ui/dialog';
import {
  ChevronLeft,
  ChevronRight,
  Shield,
  Download,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  BarChart3,
  Settings,
  X,
  Plus,
  Package,
  Home,
  GitBranch,
  FolderOpen,
  Lock,
  XIcon,
  Save,
  Globe,
  Search,
  Trash2,
  Github,
  Upload
} from 'lucide-react';

import {
  SEVERITY_LEVELS,
  POLICY_STANDARDS,
  TECHNOLOGY_STACKS,
  REPO_PROVIDERS,
  ORT_SCAN_TYPES,
  ORT_PACKAGE_MANAGERS,
  ORT_FAIL_ON_OPTIONS,
  ORT_RUN_STAGES,
  getORTConfigTemplate,
  DEVELOPMENT_PHASE_OPTIONS,
  APPLICATION_TYPE_OPTIONS,
} from '../helpers/collections';
import { useApplicationList } from '@/hooks/useApplications';
import { useProjectList } from '@/hooks/useProjects';
import {
  usePolicies,
} from '@/hooks/usePolicies';
import { CreateProjectData, ProjectAPI } from '@/api/projects';
import { CreateApplicationData, ApplicationAPI } from '@/api/applications';
import { ScanAPI, ScanType, OrtScanConfiguration, SurveyQuestion } from '@/api/scans';
import { SearchableSelect } from './ui/searchable-select';
import { api, PolicyAPI } from '@/api';
import { ProfileAPI } from '@/api/profile';
import { useAuth } from '@/contexts/AuthContext';
import { useHasPermission } from '@/hooks/useHasPermission';
import { EntitlementGate } from './EntitlementGate';
import { useNavigate } from 'react-router-dom';
import { PackageLimitsAPI } from '@/api/package-limits';
import { isSubscriptionValid, parseGitHubUrl } from '@/helpers/common';
import { SCAN_RESULT_ACCEPT, isAcceptedScanResultFile, validateScanResultFile } from '@/helpers/scanResultUpload';

const LICENSE_RISK_CATEGORIES = {
  'none': {
    name: 'No Risk',
    description: 'Licenses with no compliance concerns',
    color: 'bg-gray-50 text-gray-800 border-gray-200',
    severity: 'info',
    examples: ['Public Domain', 'CC0-1.0', 'Unlicense']
  },
  'low': {
    name: 'Low Risk',
    description: 'Permissive licenses with minimal restrictions',
    color: 'bg-green-50 text-green-800 border-green-200',
    severity: 'success',
    examples: ['MIT', 'Apache-2.0', 'BSD-2-Clause', 'BSD-3-Clause', 'ISC']
  },
  'medium': {
    name: 'Medium Risk',
    description: 'Licenses requiring legal review or compliance measures',
    color: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    severity: 'warning',
    examples: ['GPL-2.0', 'LGPL-2.1', 'MPL-1.1', 'EPL-1.0', 'EUPL-1.1']
  },
  'high': {
    name: 'High Risk',
    description: 'Strong copyleft licenses with significant obligations',
    color: 'bg-orange-50 text-orange-800 border-orange-200',
    severity: 'error',
    examples: ['GPL-3.0', 'LGPL-3.0', 'AGPL-3.0', 'MPL-2.0']
  },
  'critical': {
    name: 'Critical Risk',
    description: 'Prohibited or highly restrictive licenses',
    color: 'bg-red-50 text-red-800 border-red-200',
    severity: 'critical',
    examples: ['GPL-v2', 'OSL-3.0', 'Commercial', 'Proprietary']
  }
};

const formatLicenseId = (licenseId: string): string => {
  return licenseId
    .replace(/-/g, '-')
    .replace(/v(\d)/g, ' v$1')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toUpperCase();
};

const groupPolicyConfigByRisk = (config: Record<string, string>) => {
  const grouped: Record<string, string[]> = {};

  Object.entries(config).forEach(([licenseId, riskLevel]) => {
    if (!grouped[riskLevel]) {
      grouped[riskLevel] = [];
    }
    grouped[riskLevel].push(licenseId);
  });

  return grouped;
};


interface NewScanProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (route: string, params?: any) => void;
  workgroupId: number;
  applicationId?: number;
  projectId?: number;
  existingScan?: any;
  defaultScanMethod?: 'github' | 'manual';
}

interface ScanParameters {
  ProjectPurpose?: string;
  EndUser?: string;
  Deployment?: string;
  LicenseCategory?: string;
  Scale?: string;
  BusinessImpact?: string;
  BusinessCriticality?: string;
}

export function NewScan({ workgroupId, isOpen, onClose, onNavigate, applicationId, projectId, existingScan, defaultScanMethod }: NewScanProps) {

  const { data: applications, refetch: refetchApplications } = useApplicationList(workgroupId);
  const { data: projects, refetch: refetchProjects } = useProjectList(workgroupId);

  const { data: allPolicies, loading: policiesLoading, refetch: refetchPolicies } = usePolicies(workgroupId);
  const canCreatePolicy = useHasPermission('policies', 'create');
  const canDeletePolicy = useHasPermission('policies', 'delete');
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);

  const getInitialScanData = () => {

    let projectName, repourl, repousername;
    if (projectId) {
      const project = projects?.find(p => p.id.toString() === projectId.toString());
      projectName = project?.name || '';
      repourl = project?.repourl || '';
      repousername = project?.repousername || '';

    }

    const baseData = {
      selectedApplication: applicationId ? applicationId.toString() : '',
      selectedProject: projectId ? projectId.toString() : '',
      projectName: projectName || '',
      repourl: repourl || '',
      version: '',
      description: '',
      branchname: 'main',
      technology: [] as string[],
      applicationName: '',
      applicationDescription: '',
      applicationPhase: '',
      applicationType: '',
      businessCriticality: '',

      // Never prefilled from a project response -- the API no longer returns the token
      // ciphertext. Only set when the user pastes a fresh token or completes GitHub OAuth
      // this session, as an override for this scan (see isRepoAuthenticated/startScan).
      repoaccesstoken: '',
      repoprovider: 'GitHub',
      repotype: 'Public',
      repoid: 0,
      repousername: repousername || '',
      // True once GitHub OAuth has put a token on the profile/project server-side; the
      // token itself is never sent to the browser, so repoaccesstoken stays empty.
      githubConnectedViaProfile: false,

      scanType: ScanType.ORT_LICENSE_SCAN,
      ortScanType: '',
      ortPackageManagers: [] as string[],
      ortFailOn: 'violations',
      ortRunStages: [] as string[],
      scanparameters: {} as ScanParameters,
      selectedPolicy: '',
      policyType: 'default',
      customPolicyStandards: {} as Record<string, string>,
      customPolicyName: '',
      saveCustomPolicy: false,
      scanProgress: 0,
      logs: [] as { message: string; timestamp: number }[],
      scanId: null as number | null,
      configurationSaved: false,
      scanMethod: (defaultScanMethod || '') as '' | 'github' | 'manual',
      ortUploadFile: null as File | null,
    };

    if (existingScan) {
      const projectFromList = projects?.find(project => project.id === existingScan.projectid);
      const projectFromScan = existingScan.projects;
      const resolvedProject = projectFromList || projectFromScan || null;

      const resolvedApplicationId = existingScan.applicationid ?? resolvedProject?.applicationid;
      const resolvedPolicyId = existingScan.policyid;

      const rawTechnology = resolvedProject?.technology || existingScan.technology;
      const parsedTechnology = Array.isArray(rawTechnology)
        ? rawTechnology
        : (rawTechnology ? rawTechnology.split(',').map((tech: string) => tech.trim()).filter(Boolean) : []);

      const rawRepoId = resolvedProject?.repoid ?? existingScan.repoid;
      const parsedRepoId = typeof rawRepoId === 'string'
        ? parseInt(rawRepoId, 10)
        : typeof rawRepoId === 'number'
          ? rawRepoId
          : baseData.repoid;

      const scanTypeMap: Record<string, ScanType> = {
        'ort_license_scan': ScanType.ORT_LICENSE_SCAN,
        'ort_full_scan': ScanType.ORT_FULL_SCAN,
        'ort_vulnerability_scan': ScanType.ORT_VULNERABILITY_SCAN,
        'license_only': ScanType.LICENSE_ONLY,
        'vulnerability_only': ScanType.VULNERABILITY_ONLY,
        'full': ScanType.FULL,
        'incremental': ScanType.INCREMENTAL
      };
      const normalizedScanType = existingScan.scantype ? String(existingScan.scantype).toLowerCase() : '';
      const resolvedScanType = scanTypeMap[normalizedScanType] || baseData.scanType;

      const logEntries = typeof existingScan.scanlog === 'string'
        ? existingScan.scanlog
          .split(/\r?\n/)
          .map((line: string) => line.trim())
          .filter(Boolean)
          .map((message: string) => ({
            message,
            timestamp: existingScan.startdate ? new Date(existingScan.startdate).getTime() : Date.now()
          }))
        : baseData.logs;

      return {
        ...baseData,
        scanId: existingScan.id ?? null,
        selectedProject: existingScan.projectid ? existingScan.projectid.toString() : '',
        selectedApplication: resolvedApplicationId ? resolvedApplicationId.toString() : '',
        selectedPolicy: resolvedPolicyId ? resolvedPolicyId.toString() : '',
        policyType: resolvedPolicyId ? 'default' : 'custom',
        projectName: resolvedProject?.name || existingScan.project_name || baseData.projectName,
        version: existingScan.version ? String(existingScan.version) : baseData.version,
        // Rescans replay the exact repo this scan originally ran against, not wherever the
        // project currently points -- snapshot fields win; resolvedProject (live) is only a
        // fallback for scans that predate these columns and have no snapshot at all.
        repourl: existingScan.scanrepourl || existingScan.repourl || resolvedProject?.repourl || baseData.repourl,
        description: resolvedProject?.description || existingScan.description || baseData.description,
        technology: parsedTechnology,
        repoprovider: resolvedProject?.repoprovider || baseData.repoprovider,
        repotype: resolvedProject?.repotype || baseData.repotype,
        repoid: Number.isNaN(parsedRepoId) ? baseData.repoid : parsedRepoId,
        repousername: resolvedProject?.repousername || baseData.repousername,
        // No override here -- an existing (non-custom) scan re-run authenticates with its
        // scan's own credential if it has one, else its project's, resolved server-side.
        repoaccesstoken: baseData.repoaccesstoken,
        scanparameters: existingScan.scanparameters || baseData.scanparameters,
        scanType: resolvedScanType,
        scanMethod: existingScan.scanorigin
          || existingScan.scanparameters?.scanMethod
          || (existingScan.repourl ? 'github' : 'manual'),
        scanProgress: typeof existingScan.progresspercentage === 'number' ? existingScan.progresspercentage : baseData.scanProgress,
        logs: logEntries,
        configurationSaved: true
      };
    }


    return baseData;
  };

  const [scanData, setScanData] = useState(getInitialScanData());
  const { user, clientProfile, subscription, refreshUser } = useAuth();
  const { isConnected, scanProgress, subscribeTo, unsubscribeFrom } = useScanMonitor();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [scanCancelled, setScanCancelled] = useState(false);
  const [lastProcessedProgress, setLastProcessedProgress] = useState<{
    scanId: number | null;
    progress: number;
    status: string;
    message?: string;
    timestamp: number;
  } | null>(null);
  const [showPolicyDetails, setShowPolicyDetails] = useState<string | null>(null);
  const [showPolicyInfo, setShowPolicyInfo] = useState<string | null>(null);
  const [isSavingConfiguration, setIsSavingConfiguration] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [policySearchTerm, setPolicySearchTerm] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploadingOrt, setIsUploadingOrt] = useState(false);
  const [ortValidationError, setOrtValidationError] = useState<string | null>(null);
  const [manualUploadLogs, setManualUploadLogs] = useState<{ message: string; timestamp: number }[]>([]);
  const [manualUploadProgress, setManualUploadProgress] = useState(0);
  const [manualUploadComplete, setManualUploadComplete] = useState(false);
  const manualUploadLogsEndRef = useRef<HTMLDivElement>(null);

  // Fetched from the backend (single source of truth — see api/apps/client/scans/services/scan.service.ts
  // getSurveyQuestions) instead of a hardcoded local copy, which had drifted out of sync with the
  // backend's own question/option definitions.
  const [surveyQuestions, setSurveyQuestions] = useState<SurveyQuestion[]>([]);
  const [isLoadingSurveyQuestions, setIsLoadingSurveyQuestions] = useState(true);

  useEffect(() => {
    let cancelled = false;
    ScanAPI.getSurveyQuestions()
      .then(response => {
        if (!cancelled) {
          setSurveyQuestions(response.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Failed to load survey questions. Please refresh and try again.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSurveyQuestions(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stepNames = scanData.scanMethod === 'manual'
    ? ['Project Details', 'Business Impact', 'Upload & Results']
    : ['Project Details', 'Business Impact', 'License Policy', 'Scan Progress', 'Results'];

  // Internal step numbers to display in the indicator
  const displaySteps = scanData.scanMethod === 'manual' ? [1, 2, 4] : [1, 2, 3, 4, 5];

  const [scanResults, setScanResults] = useState<{
    summary: {
      totalComponents: number;
      totalEncryptionComponents: number;
      totalLicenseIssues: number;
      vulnerabilitiesFound: number;
      exploitableVulnerabilities: number;
      provenance: number;
    };
    riskScores: {
      businessRisk: number;
      totalTechnicalDebt: number;
      overallRiskScore: number;
      operationalRiskScore: number;
      vulnerabilityRiskScore: number;
      licenseRiskScore: number;
    };
    reports: {
      ortReportUrl?: string;
      sccReportUrl?: string;
    };
  } | null>(null);

  useEffect(() => {

    if (projectId && projects && !existingScan) {
      const selectedProject = projects.find(p => p.id === projectId);
      if (selectedProject) {
        setScanData(prev => ({
          ...prev,
          selectedProject: projectId.toString(),
          selectedApplication: selectedProject.applicationid ? selectedProject.applicationid.toString() : prev.selectedApplication,
          projectName: selectedProject.name || '',
          repourl: selectedProject.repourl || '',
          description: selectedProject.description || '',
          repoprovider: selectedProject.repoprovider || 'GitHub',
          repotype: selectedProject.repotype || 'Public',
          // No override -- the project's own token (if any) is applied server-side by default.
          repoaccesstoken: '',
          repousername: selectedProject.repousername || '',
          githubConnectedViaProfile: false,
          technology: selectedProject.technology ?
            (typeof selectedProject.technology === 'string' ?
              JSON.parse(selectedProject.technology) :
              selectedProject.technology) : [],
        }));
      }
    }
  }, [projectId, projects, existingScan]);

  useEffect(() => {
    if (applicationId && applications && !existingScan && !projectId) {
      setScanData(prev => ({
        ...prev,
        selectedApplication: applicationId.toString(),
      }));
    }
  }, [applicationId, applications, existingScan, projectId]);

  const systemPolicies = allPolicies?.filter(policy => policy.isdefault) || [];
  const customPolicies = allPolicies?.filter(policy => !policy.isdefault) || [];

  // Auto-select the Standard system policy so the user isn't required to click
  // one -- safe now that there is always exactly one system policy. Skipped
  // for rescans/edits, which already resolve their own policy in getInitialScanData.
  useEffect(() => {
    if (
      !existingScan &&
      !policiesLoading &&
      scanData.policyType === 'default' &&
      scanData.selectedPolicy === '' &&
      systemPolicies.length > 0
    ) {
      setScanData(prev => ({ ...prev, selectedPolicy: systemPolicies[0].id.toString() }));
    }
  }, [existingScan, policiesLoading, scanData.policyType, scanData.selectedPolicy, systemPolicies]);

  const filteredSystemPolicies = systemPolicies.filter(policy =>
    policySearchTerm === '' ||
    policy.name.toLowerCase().includes(policySearchTerm.toLowerCase()) ||
    policy.description?.toLowerCase().includes(policySearchTerm.toLowerCase()) ||
    policy.policytype.toLowerCase().includes(policySearchTerm.toLowerCase()) ||
    policy.severity?.toLowerCase().includes(policySearchTerm.toLowerCase())
  );

  const filteredCustomPolicies = customPolicies.filter(policy =>
    policySearchTerm === '' ||
    policy.name.toLowerCase().includes(policySearchTerm.toLowerCase()) ||
    policy.description?.toLowerCase().includes(policySearchTerm.toLowerCase()) ||
    policy.policytype.toLowerCase().includes(policySearchTerm.toLowerCase()) ||
    policy.severity?.toLowerCase().includes(policySearchTerm.toLowerCase())
  );

  const checkForConflictingScans = async (projectId: string, version: string) => {
    if (!projectId || !version.trim()) return;

    try {
      const scansResponse = await ScanAPI.getAll({ projectId: parseInt(projectId) });
      if (scansResponse.data && scansResponse.data.items) {
        const conflictingScans = scansResponse.data.items.filter((scan: any) =>
          scan.version === version &&
          (scan.status === 'running' || scan.scanstatus === 'running' || scan.scanstatus === 'pending') &&
          scan.id !== scanData.scanId
        );

        if (conflictingScans.length > 0) {
          const runningScan = conflictingScans[0];
          toast.warning(
            `⚠️ Another scan for version "${version}" is already running (Scan #${runningScan.id}). ` +
            `Please wait for it to complete or choose a different version.`
          );
          return true;
        }
      }
    } catch (error) {
    }

    return false;
  };

  const validateScanParameters = () => {
    const missingParameters: string[] = [];

    surveyQuestions.forEach(question => {
      if (!scanData.scanparameters[question.type]) {
        missingParameters.push(question.question);
      }
    });

    return missingParameters;
  };
  const checkAndResumeRunningScan = async (scanId: number) => {
    try {
      const scanResponse = await ScanAPI.getById(scanId);
      if (scanResponse.data) {
        const currentScan = scanResponse.data;

        const isCurrentlyRunning = currentScan.scanstatus === 'running' ||
          currentScan.scanstatus === 'pending';

        if (isCurrentlyRunning) {

          setIsScanning(true);
          setScanComplete(false);
          setScanCancelled(false);

          const currentProgress = currentScan.progresspercentage || 0;
          setScanData(prev => ({
            ...prev,
            scanProgress: currentProgress,
            logs: [
              ...prev.logs,
              {
                message: `📡 Resuming scan monitoring... (Current progress: ${currentProgress}%)`,
                timestamp: Date.now()
              }
            ]
          }));

          if (!isSubscribed) {
            subscribeTo(scanId);
            setIsSubscribed(true);

            setScanData(prev => ({
              ...prev,
              logs: [
                ...prev.logs,
                {
                  message: '🔌 Reconnected to real-time progress monitoring',
                  timestamp: Date.now()
                }
              ]
            }));
          }

          return true;
        } else if (currentScan.scanstatus === 'completed') {
          setIsScanning(false);
          setScanComplete(true);
          setScanCancelled(false);

          setScanData(prev => ({
            ...prev,
            scanProgress: 100,
            logs: [
              ...prev.logs,
              {
                message: '✅ Scan was completed while you were away',
                timestamp: Date.now()
              }
            ]
          }));

          fetchScanResults(scanId);

        } else if (currentScan.scanstatus === 'cancelled' || currentScan.scanstatus === 'failed') {
          setIsScanning(false);
          setScanComplete(false);
          setScanCancelled(currentScan.scanstatus === 'cancelled');

          setScanData(prev => ({
            ...prev,
            scanProgress: 0,
            logs: [
              ...prev.logs,
              {
                message: `❌ Scan was ${currentScan.scanstatus} while you were away`,
                timestamp: Date.now()
              }
            ]
          }));
        }
      }
    } catch (error) {
      setScanData(prev => ({
        ...prev,
        logs: [
          ...prev.logs,
          {
            message: '⚠️ Unable to check current scan status - please refresh if scan appears stuck',
            timestamp: Date.now()
          }
        ]
      }));
    }

    return false;
  };

  const [wasOpen, setWasOpen] = useState(false);

  useEffect(() => {
    if (isOpen && !wasOpen) {
      setScanData(getInitialScanData());
      setScanResults(null);
      setIsSubscribed(false);
      setLastProcessedProgress(null);
      setError(null);
      setValidationErrors({});
      setPolicySearchTerm('');
      setCurrentStep(existingScan ? 4 : 1);
      setIsSavingConfiguration(false);

      setIsScanning(false);
      setScanComplete(false);
      setScanCancelled(false);

      if (existingScan && existingScan.id) {
        if (existingScan.scanstatus === 'completed') {
          fetchScanResults(existingScan.id);
        } else if (
          existingScan.scanorigin === 'github' ||
          (!existingScan.scanorigin && (
            existingScan.scanparameters?.scanMethod === 'github' ||
            (!existingScan.scanparameters?.scanMethod && existingScan.repourl)
          ))
        ) {
          checkAndResumeRunningScan(existingScan.id);
        }
        // manual rescans: just show the fresh upload UI — nothing to resume
      }

      setIsCancelling(false);
      setIsUploadingOrt(false);
      setManualUploadLogs([]);
      setManualUploadProgress(0);
      setManualUploadComplete(false);
      setOrtValidationError(null);
      setWasOpen(true);
    } else if (!isOpen && wasOpen) {
      setIsSavingConfiguration(false);
      setWasOpen(false);
    }
  }, [isOpen, existingScan]);

  useEffect(() => {
    if (scanData.scanId && scanProgress[scanData.scanId]) {
      const progress = scanProgress[scanData.scanId];
      const currentTimestamp = Date.now();

      const progressKey = `${scanData.scanId}-${progress.progress}-${progress.status}-${progress.message || ''}`;
      const lastProgressKey = lastProcessedProgress
        ? `${lastProcessedProgress.scanId}-${lastProcessedProgress.progress}-${lastProcessedProgress.status}-${lastProcessedProgress.message || ''}`
        : null;

      if (progressKey === lastProgressKey) {
        return;
      }

      if (lastProcessedProgress &&
        lastProcessedProgress.scanId === scanData.scanId &&
        lastProcessedProgress.progress === progress.progress &&
        lastProcessedProgress.status === progress.status &&
        (currentTimestamp - lastProcessedProgress.timestamp) < 1000) {
        return;
      }


      setLastProcessedProgress({
        scanId: scanData.scanId,
        progress: progress.progress,
        status: progress.status,
        message: progress.message,
        timestamp: currentTimestamp
      });

      const progressMessage = `📡 ${progress.message || `${progress.status} - ${progress.progress}%`}`;

      setScanData(prev => {
        const updatedLogs = [
          ...prev.logs,
          { message: progressMessage, timestamp: currentTimestamp }
        ];

        updateScanlogInDatabase(updatedLogs);

        return {
          ...prev,
          scanProgress: progress.progress,
          logs: updatedLogs
        };
      });

      if (progress.status === 'completed') {
        setIsScanning(false);
        setScanComplete(true);

        if (scanData.scanId) {
          fetchScanResults(scanData.scanId);
        }
        if (isSubscribed) {
          unsubscribeFrom(scanData.scanId);
          setIsSubscribed(false);
        }

        toast.success('Scan completed successfully!');

        setScanData(prev => ({
          ...prev,
          logs: [...prev.logs, { message: 'Scan completed successfully! Results are now available.', timestamp: Date.now() }]
        }));

      } else if (progress.status === 'failed' || progress.status === 'cancelled') {
        setIsScanning(false);
        setScanCancelled(progress.status === 'cancelled');

        if (isSubscribed) {
          unsubscribeFrom(scanData.scanId);
          setIsSubscribed(false);
        }

        const message = progress.error || `Scan ${progress.status.toLowerCase()}`;
        toast.error(`❌ ${message}`);

        setScanData(prev => ({
          scanProgress: 0,
          ...prev,
          logs: [...prev.logs, { message: `❌ ${message}`, timestamp: Date.now() }]
        }));
      }
    }
  }, [scanProgress, scanData.scanId, unsubscribeFrom, isSubscribed]);

  // WebSocket progress handler for manual ORT upload
  useEffect(() => {
    if (scanData.scanMethod !== 'manual' || !scanData.scanId || !isUploadingOrt) return;
    const progress = scanProgress[scanData.scanId];
    if (!progress) return;

    const msg = `📡 ${progress.message || `${progress.status} - ${progress.progress}%`}`;
    setManualUploadProgress(progress.progress);
    setManualUploadLogs(prev => {
      // Deduplicate: skip if same message as last entry
      if (prev.length > 0 && prev[prev.length - 1].message === msg) return prev;
      return [...prev, { message: msg, timestamp: Date.now() }];
    });

    if (progress.status === 'completed') {
      setIsUploadingOrt(false);
      unsubscribeFrom(scanData.scanId);
      setManualUploadLogs(prev => [...prev, { message: '✅ Processing complete! Loading results...', timestamp: Date.now() }]);
      setTimeout(async () => {
        if (scanData.scanId) await fetchScanResults(scanData.scanId);
        setManualUploadComplete(true);
      }, 800);
    } else if (progress.status === 'failed') {
      setIsUploadingOrt(false);
      unsubscribeFrom(scanData.scanId);
      setManualUploadLogs(prev => [...prev, { message: `❌ ${progress.error || 'Processing failed'}`, timestamp: Date.now() }]);
      toast.error(progress.error || 'ORT processing failed');
    }
  }, [scanProgress, scanData.scanId, scanData.scanMethod, isUploadingOrt]);

  // Auto-scroll logs to bottom
  useEffect(() => {
    manualUploadLogsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [manualUploadLogs]);

  useEffect(() => {
    return () => {
      if (scanData.scanId && isSubscribed) {
        unsubscribeFrom(scanData.scanId);
        setIsSubscribed(false);
        setLastProcessedProgress(null);
      }
    };
  }, [scanData.scanId, unsubscribeFrom, isSubscribed]);

  const toggleArrayValue = (field: 'technology', value: string) => {
    setScanData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };


  const updateProjectData = (field: string, value: string) => {
    setScanData(prev => ({
      ...prev,
      [field]: value
    }));

  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 1:
        return 'Enter your project name and GitHub repository URL for scanning';
      case 2:
        return 'Help us understand your project scope and usage patterns';
      case 3:
        return 'Choose the license compliance policy that best fits your legal requirements';
      case 4:
        return 'Select scan type and monitor the progress of your scan';
      case 5:
        return 'Review scan results and download reports';
      default:
        return 'Configure your scan';
    }
  };

  const saveScanConfiguration = async () => {
    setIsSavingConfiguration(true);
    try {
      const versionError = await validateVersionWithServer(scanData.version, scanData.selectedProject);
      if (versionError) {
        throw new Error(versionError);
      }

      let applicationId = null;
      const wasCustomApplication = scanData.selectedApplication === 'custom';

      if (wasCustomApplication) {
        const createdApplication = await createNewApplication();
        applicationId = createdApplication?.id;
        refetchApplications();
      } else {
        applicationId = parseInt(scanData.selectedApplication);
      }

      if (!applicationId) {
        throw new Error('Failed to get or create application');
      }

      let projectId = null;
      const wasCustomProject = scanData.selectedProject === 'custom';

      if (wasCustomProject) {
        const createdProject = await createNewProject(applicationId);
        projectId = createdProject?.id;
        refetchProjects();
      } else {
        projectId = parseInt(scanData.selectedProject);
      }

      if (!projectId) {
        throw new Error('Failed to get or create project');
      }

      setScanData(prev => ({
        ...prev,
        selectedApplication: applicationId!.toString(),
        selectedProject: projectId!.toString()
      }));

      const scanConfigData = {
        workgroupid: workgroupId,
        applicationid: parseInt(scanData.selectedApplication),
        projectid: projectId,
        version: scanData.version,
        scantype: scanData.scanType,
        scanrepourl: scanData.repourl,
        scanparameters: {
          ...scanData.scanparameters,
          scanMethod: scanData.scanMethod,
        },
        description: `Scan for ${scanData.projectName}`,
        policyid: scanData.selectedPolicy ? parseInt(scanData.selectedPolicy) : undefined,
        status: 'configured',
      };

      const response = await ScanAPI.create(scanConfigData);
      if (response.data) {

        setScanData(prev => ({
          ...prev,
          scanId: response.data?.id || null,
          selectedProject: projectId!.toString(),
          selectedApplication: applicationId!.toString(),
          configurationSaved: true
        }));

        return response.data;
      }

    } catch (error) {
      throw error;
    } finally {
      setIsSavingConfiguration(false);
    }
  };

  const updateScanData = (field: string, value: string) => {
    setScanData(prev => ({
      ...prev,
      [field]: value
    }));
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
    if (error) {
      setError(null);
    }
  };

  const handleManualUpload = async () => {
    if (!scanData.ortUploadFile || !scanData.scanId) return;
    setOrtValidationError(null);
    const validationError = await validateScanResultFile(scanData.ortUploadFile);
    if (validationError) {
      setOrtValidationError(validationError);
      toast.error(validationError);
      return;
    }

    // Reset log state and subscribe to WebSocket before upload
    setManualUploadLogs([{ message: `Uploading ${scanData.ortUploadFile.name} (${(scanData.ortUploadFile.size / 1024 / 1024).toFixed(2)} MB)...`, timestamp: Date.now() }]);
    setManualUploadProgress(0);
    setManualUploadComplete(false);
    subscribeTo(scanData.scanId);
    setManualUploadLogs(prev => [...prev, { message: '📡 Real-time monitoring activated', timestamp: Date.now() }]);

    setIsUploadingOrt(true);
    try {
      const result = await ScanAPI.uploadOrtFiles(scanData.scanId, scanData.ortUploadFile);
      // upload() returns raw JSON (no .data wrapper). Any thrown exception means failure.
      // If the server returned an explicit error body, surface it.
      const serverError = (result as any)?.error || ((result as any)?.statusCode >= 400 ? (result as any)?.message : null);
      if (serverError) {
        throw new Error(serverError);
      }
      setManualUploadLogs(prev => [...prev, { message: '✅ File received by server. Processing in background...', timestamp: Date.now() }]);
      // Step advancement is handled by the WebSocket useEffect when status === 'completed'
    } catch (e) {
      toast.error('Upload failed: ' + (e instanceof Error ? e.message : 'Unknown error'));
      setIsUploadingOrt(false);
      unsubscribeFrom(scanData.scanId);
    }
  };

  const handleNext = async () => {
    if (currentStep < 5) {

      if (currentStep === 1) {

        if (scanData.selectedApplication === 'custom') {
          if (!scanData.applicationType) {
            toast.error('Please select an Application Type');
            return;
          }
          if (!scanData.businessCriticality) {
            toast.error('Please select Business Criticality');
            return;
          }
        }

        const versionError = await validateVersionWithServer(scanData.version, scanData.selectedProject);
        if (versionError) {
          toast.error(versionError);
          return;
        }
      }

      if (currentStep === 2) {
        const unansweredQuestions = surveyQuestions.filter(
          question => !scanData.scanparameters[question.type]
        );

        if (unansweredQuestions.length > 0) {
          toast.error(`Please answer all required questions (${unansweredQuestions.length} remaining)`);
          return;
        }
      }

      // Manual upload: at Step 2→4, save config and skip License Policy (Step 3)
      if (currentStep === 2 && scanData.scanMethod === 'manual') {
        if (!scanData.configurationSaved) {
          try {
            const result = await saveScanConfiguration();
            if (!result?.id) {
              toast.error('Failed to save scan configuration. Please try again.');
              return;
            }
          } catch {
            return;
          }
        }
        setCurrentStep(4);
        setTimeout(() => {
          document.querySelector('.overflow-y-auto')?.scrollTo({ top: 0, behavior: 'smooth' });
        }, 50);
        return;
      }

      // Manual upload: at Step 4, trigger file upload instead of advancing
      if (currentStep === 4 && scanData.scanMethod === 'manual') {
        await handleManualUpload();
        return;
      }

      setCurrentStep(currentStep + 1);
      setTimeout(() => {
        document.querySelector('.overflow-y-auto')?.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    }
  };

  const handlePrevious = () => {
    if (currentStep > (existingScan ? 2 : 1)) {
      // Manual upload: Step 4 → skip Step 3 → go back to Step 2
      const prevStep = (currentStep === 4 && scanData.scanMethod === 'manual') ? 2 : currentStep - 1;
      setCurrentStep(prevStep);
      setTimeout(() => {
        document.querySelector('.overflow-y-auto')?.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    }
  };

  const startScan = async () => {
    if (isScanning) {
      toast.error('Scan is already in progress');
      return;
    }

    // if (existingScan) {
    //   const isSubscriptionActive = isSubscriptionValid(subscription?.validupto || null);
    //   if (!isSubscriptionActive) {
    //     toast.error('Your subscription has expired. Please renew to perform scans.');
    //     return;
    //   }

    //   try {
    //     const validationResponse = await PackageLimitsAPI.validateScanCreation();
    //     if (!validationResponse.data?.isValid) {
    //       toast.error(validationResponse.data?.message || 'You have reached your scan limit. Please upgrade your plan.');
    //       return;
    //     }
    //   } catch (error) {
    //     toast.error('Unable to validate scan limits. Please try again.');
    //     return;
    //   }
    // }

    if (!scanData.repourl || scanData.repourl.trim() === '') {
      toast.error('Repository URL is required to start the scan');
      return;
    }

    // "Authenticated" means any of: a fresh token typed this session, GitHub OAuth just
    // completed (githubConnectedViaProfile -- the token itself never reaches the browser,
    // it's copied profile -> project server-side), or the selected project already has one
    // connected -- the API never hands back the project's stored token to check against directly.
    const selectedProjectHasToken = scanData.selectedProject !== 'custom'
      && !!projects?.find(p => p.id.toString() === scanData.selectedProject)?.hasrepoaccesstoken;
    const hasGithubToken = scanData.githubConnectedViaProfile
      || selectedProjectHasToken
      || (scanData.repoaccesstoken && scanData.repoaccesstoken.trim() !== '');
    if (!hasGithubToken || (!scanData.repousername || scanData.repousername.trim() === '')) {
      toast.error('GitHub authentication is required. Please authenticate with GitHub.');
      return;
    }

    const isValidGitHubUrl = /github\.com\/[\w-]+\/[\w-]+/i.test(scanData.repourl);
    if (!isValidGitHubUrl) {
      toast.error('Invalid GitHub repository URL. Expected format: https://github.com/owner/repo');
      return;
    }

    let scanId = scanData.scanId;
    if (!scanData.configurationSaved) {
      try {
        const result = await saveScanConfiguration();
        if (result && result.id) {
          scanId = result.id;
        }
        else {
          toast.error('Failed to save scan configuration. Please try again.');
          return;
        }
      } catch (error) {
        //toast.error('Failed to save scan configuration. Please try again.');
        return;
      }
    }

    const activeScanId = scanId ?? scanData.scanId;

    if (!activeScanId) {
      toast.error('Failed to resolve scan id. Please try again.');
      return;
    }

    if (scanData.scanId || activeScanId) {
      try {
        const currentScanResponse = await ScanAPI.getById(activeScanId);
        if (currentScanResponse.data) {
          const currentScanStatus = currentScanResponse.data.scanstatus;

          if (currentScanStatus === 'running' || currentScanStatus === 'pending') {
            toast.error('This scan is already running. Resuming monitoring...');
            await checkAndResumeRunningScan(activeScanId);
            return;
          }

          if (currentScanStatus === 'failed' || currentScanStatus === 'cancelled') {
            setIsScanning(false);
            setScanComplete(false);
            setScanCancelled(false);
          }
        }
      } catch (error) {
      }
    }

    setIsScanning(true);
    setIsSubscribed(false);
    setLastProcessedProgress(null);

    const initialLogs = [{ message: 'Initializing scan...', timestamp: Date.now() }];
    setScanData(prev => ({ ...prev, scanProgress: 0, logs: initialLogs }));
    updateScanlogInDatabase(initialLogs, activeScanId);

    try {
      setScanData(prev => {
        const updatedLogs = [...prev.logs, { message: 'Starting ORT scan with selected configuration...', timestamp: Date.now() }];
        updateScanlogInDatabase(updatedLogs, activeScanId);
        return { ...prev, logs: updatedLogs };
      });

      const scanUpdateData = {
        progresspercentage: 5,
        filesscanned: 0,
        componentsfound: 0,
        vulnerabilitiesfound: 0,
        licenseissuesfound: 0,
        businesscriticality: "low",
        businessriskscore: 0,
        operationalriskscore: 0,
        technicaldebtscore: 0,
        licensescore: 0,
        overallscore: 0,
        projectsize: 0,
        startdate: new Date(),
        errormessage: '',
        scanlog: scanData.logs.map(log => log.message).join('\n'),
        status: "running" as const
      };

      const scanResponse = await ScanAPI.update(activeScanId, scanUpdateData);
      if (scanResponse.data) {
        setScanData(prev => ({ ...prev, logs: [...prev.logs, { message: `Scan type: ${scanData.scanType}`, timestamp: Date.now() }] }));

        const ortResult = await startOrtScan(activeScanId);

        if (ortResult && ortResult.runId) {
          subscribeTo(activeScanId, ortResult.runId);
          setScanData(prev => ({
            ...prev,
            logs: [...prev.logs, { message: `📡 Monitoring ORT scan (Run ID: ${ortResult.runId})`, timestamp: Date.now() }]
          }));
        } else {
          setScanData(prev => ({
            ...prev,
            logs: [...prev.logs, { message: '❌ ORT scan failed to start properly', timestamp: Date.now() }]
          }));

          setIsScanning(false);

          try {
            await ScanAPI.update(activeScanId, {
              progresspercentage: 0,
              status: "failed" as const,
              errormessage: 'Failed to start ORT scan - no run ID returned',
              scanlog: [...scanData.logs.map(log => log.message), '❌ ORT scan failed to start properly'].join('\n'),
            });
          } catch (updateError) {
          }

          toast.error('Failed to start ORT scan. Please check your configuration and try again.');
          return;
        }
      }

    } catch (error) {


      try {
        await ScanAPI.update(activeScanId, {
          progresspercentage: 0,
          status: "failed" as const,
          errormessage: error instanceof Error ? error.message : 'Unknown error',
          scanlog: [...scanData.logs.map(log => log.message), `Scan failed: ${error instanceof Error ? error.message : 'Unknown error'}`].join('\n'),

        });
      } catch (updateError) {
      }

      setIsScanning(false);

      const errorMessage = error instanceof Error ? error.message : '';
      const isAuthError = errorMessage.includes('GitHub authentication failed') ||
        errorMessage.includes('Invalid GitHub access token') ||
        errorMessage.includes('401') ||
        errorMessage.includes('Unauthorized') ||
        errorMessage.includes('Bad credentials');

      if (isAuthError) {
        setScanData(prev => {
          const updatedLogs = [
            ...prev.logs,
            {
              message: '🔐 GitHub authentication failed. Please re-authenticate to continue.',
              timestamp: Date.now()
            }
          ];
          updateScanlogInDatabase(updatedLogs);
          return {
            ...prev,
            repoaccesstoken: '',
            repousername: '',
            repoid: 0,
            githubConnectedViaProfile: false,
            logs: updatedLogs
          };
        });

        toast.error('GitHub authentication failed. Please click "Authenticate with GitHub" to continue.', {
          duration: 3000,

        });
      } else {
        toast.error(`Scan failed: ${errorMessage || 'Unknown error occurred'}`);
      }

    }
  };

  const cancelScan = async () => {
    try {
      setIsCancelling(true);
      setScanData(prev => {
        const updatedLogs = [...prev.logs, { message: 'Cancelling scan...', timestamp: Date.now() }];
        updateScanlogInDatabase(updatedLogs);
        return { ...prev, logs: updatedLogs };
      });

      if (scanData.scanId) {
        try {
          await ScanAPI.cancelOrtScan(scanData.scanId);
          setScanData(prev => ({ ...prev, logs: [...prev.logs, { message: 'Sent cancellation request to GitHub Actions...', timestamp: Date.now() }] }));
        } catch (cancelError) {
          setScanData(prev => ({ ...prev, logs: [...prev.logs, { message: 'Could not cancel remote workflow, updating local status...', timestamp: Date.now() }] }));
        }

        await ScanAPI.update(scanData.scanId, {
          status: "cancelled" as const,
          progresspercentage: 0,
          errormessage: 'Scan cancelled by user',
          scanlog: [...scanData.logs.map(log => log.message), 'Scan cancelled by user'].join('\n'),
          completedate: new Date(),
        });

        if (isSubscribed) {
          unsubscribeFrom(scanData.scanId);
          setIsSubscribed(false);
          setLastProcessedProgress(null);
        }
      }

      setScanData(prev => {
        const updatedLogs = [...prev.logs, { message: '✅ Scan cancelled successfully', timestamp: Date.now() }];
        updateScanlogInDatabase(updatedLogs);
        return {
          ...prev,
          logs: updatedLogs
        };
      });

      setScanCancelled(true);
      setIsScanning(false);

      toast.success('Scan cancelled successfully');

    } catch (error) {
      setScanData(prev => {
        const updatedLogs = [...prev.logs, { message: `Failed to cancel scan: ${error instanceof Error ? error.message : 'Unknown error'}`, timestamp: Date.now() }];
        updateScanlogInDatabase(updatedLogs);
        return {
          ...prev,
          logs: updatedLogs
        };
      });
      toast.error('Failed to cancel scan');
    } finally {
      setIsCancelling(false);
    }
  };


  const startOrtScan = async (scanId: number) => {
    try {
      setScanData(prev => {
        const updatedLogs = [...prev.logs, { message: 'Configuring ORT scan...', timestamp: Date.now() }];
        updateScanlogInDatabase(updatedLogs);
        return { ...prev, logs: updatedLogs };
      });

      const getOrtConfigForScanType = (scanType: string) => {


        const getPolicyParameter = () => {
          if (scanData.policyType === 'custom') {
            return 'custom';
          }

          const selectedPolicyId = scanData.selectedPolicy;
          if (!selectedPolicyId) {
            return 'default';
          }

          const selectedPolicy = allPolicies?.find(p => p.id.toString() === selectedPolicyId);
          if (!selectedPolicy) {
            return 'default';
          }

          switch (selectedPolicy.severity?.toLowerCase()) {
            case 'critical':
              return 'strict';
            case 'high':
              return 'strict';
            case 'medium':
              return 'default';
            case 'low':
              return 'lenient';
            default:
              return 'default';
          }
        };
        const selectedPolicy = allPolicies?.find(p => p.id.toString() === scanData.selectedPolicy);


        const baseConfig = {
          scanId: scanId,
          policy: getPolicyParameter(),

        };

        switch (scanType) {
          case ScanType.ORT_FULL_SCAN:
            return {
              ...baseConfig,
              scan_type: 'full',
            };
          case ScanType.ORT_LICENSE_SCAN:
          default:
            return {
              ...baseConfig,
              scan_type: 'standard',

            };
        }
      };

      const { mainUrl, version } = parseGitHubUrl(scanData.repourl);

      const ortConfig: OrtScanConfiguration = {
        repourl: scanData.repourl,
        repourlmain: mainUrl,
        repoversion: version,
        branchname: scanData.branchname,
        repoaccesstoken: scanData.repoaccesstoken,
        repousername: scanData.repousername,

        ort_config: {
          ...getOrtConfigForScanType(scanData.scanType),

        },
        webhook_url: `${window.location.origin}/api/scans/ort-callback`
      };

      setScanData(prev => {
        const updatedLogs = [...prev.logs, { message: 'Starting ORT scan...', timestamp: Date.now() }];
        updateScanlogInDatabase(updatedLogs);
        return { ...prev, logs: updatedLogs };
      });

      const ortResponse = await ScanAPI.startOrtScan(ortConfig);

      if (ortResponse.data && ortResponse.data.runId) {
        setScanData(prev => {
          const updatedLogs = [...prev.logs, { message: `ORT scan initiated. External run ID: ${ortResponse.data.runId}`, timestamp: Date.now() }];
          updateScanlogInDatabase(updatedLogs);
          return {
            ...prev,
            logs: updatedLogs
          };
        });

        setScanData(prev => {
          const updatedLogs = [...prev.logs, { message: 'WebSocket monitoring will be activated...', timestamp: Date.now() }];
          updateScanlogInDatabase(updatedLogs);
          return {
            ...prev,
            logs: updatedLogs
          };
        });

        return { runId: ortResponse.data.runId };
      }
      else {
        const errorMsg = 'Failed to start ORT scan - GitHub workflow did not initialize properly';
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }

    } catch (error) {

      let userMessage = 'Failed to start ORT scan';
      let detailedMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      if (detailedMessage.includes('GitHub authentication failed') ||
        detailedMessage.includes('401') ||
        detailedMessage.includes('Unauthorized')) {
        userMessage = 'GitHub authentication failed';
        detailedMessage = 'Please verify your GitHub access token and try again.';
      } else if (detailedMessage.includes('403') || detailedMessage.includes('Forbidden')) {
        userMessage = 'GitHub access denied';
        detailedMessage = 'You do not have permission to access this repository. Please check repository permissions.';
      } else if (detailedMessage.includes('404') || detailedMessage.includes('Not Found')) {
        userMessage = 'Repository not found';
        detailedMessage = 'The specified repository could not be found. Please verify the repository URL.';
      } else if (detailedMessage.includes('rate limit')) {
        userMessage = 'GitHub API rate limit exceeded';
        detailedMessage = 'Please wait a few minutes before trying again or use an authenticated token.';
      } else if (detailedMessage.includes('network') || detailedMessage.includes('timeout')) {
        userMessage = 'Network connection failed';
        detailedMessage = 'Unable to connect to GitHub. Please check your internet connection and try again.';
      }

      setScanData(prev => {
        const updatedLogs = [...prev.logs, {
          message: `❌ ${userMessage}: ${detailedMessage}`,
          timestamp: Date.now()
        }];
        updateScanlogInDatabase(updatedLogs);
        return {
          ...prev,
          logs: updatedLogs
        };
      });

      toast.error(`${userMessage}: ${detailedMessage}`);
      throw error;
    }
  };

  const fetchScanResults = async (scanId: number) => {
    try {
      const scanResponse = await ScanAPI.getById(scanId);
      if (scanResponse.data) {
        const scan = scanResponse.data;

        setScanResults({
          summary: {
            totalComponents: scan.componentsfound || 0,
            totalEncryptionComponents: scan.encryption_count || 0,
            totalLicenseIssues: scan.licenseissuesfound || 0,
            vulnerabilitiesFound: scan.vulnerabilitiesfound || 0,
            exploitableVulnerabilities: scan.exploitable_vulnerabilities || 0,
            provenance: scan.provenance_count || 0,
          },
          riskScores: {
            businessRisk: scan.businessriskscore || 0,
            totalTechnicalDebt: scan.technicaldebtscore || 0,
            overallRiskScore: scan.overallscore || 0,
            operationalRiskScore: scan.operationalriskscore || 0,
            vulnerabilityRiskScore: scan.vulnerabilityscore || 0,
            licenseRiskScore: scan.licensescore || 0,
          },
          reports: {
            ortReportUrl: `/api/scans/${scanId}/download/ort`,
            sccReportUrl: `/reports/scc/${scanId}`,
          }
        });
      }
    } catch (error) {
    }
  };

  const updateScanlogInDatabase = async (logs: Array<{ message: string, timestamp: number }>, currentScanId?: number | null) => {
    const targetScanId = currentScanId ?? scanData.scanId;

    if (targetScanId && logs.length > 0) {
      try {
        const scanlogContent = logs.map(log => `[${new Date(log.timestamp).toLocaleString()}] ${log.message}`).join('\n');
        await ScanAPI.update(targetScanId, {
          scanlog: scanlogContent
        });
      } catch (error) {
        console.error('Failed to update scanlog in database:', error);
      }
    }
  };



  const [versionValidation, setVersionValidation] = useState<{ isValid: boolean; message?: string } | null>(null);
  const [isValidatingVersion, setIsValidatingVersion] = useState(false);

  const validateVersionWithServer = async (version: string, projectId: string): Promise<string | null> => {
    if (!version.trim()) {
      return 'Version is required';
    }

    if (projectId === 'custom') {
      return null;
    }

    try {
      setIsValidatingVersion(true);
      const response = await ScanAPI.validateVersion(
        parseInt(projectId),
        version.trim(),
        existingScan?.id
      );

      if (response.data) {
        return response.data.isValid ? null : response.data.message || 'Version validation failed';
      }

      return 'Version validation failed';
    } catch (error) {
      return 'Unable to validate version. Please try again.';
    } finally {
      setIsValidatingVersion(false);
    }
  };

  const getVersionValidationError = () => {
    if (!scanData.version.trim()) {
      return 'Version is required';
    }

    if (versionValidation && !versionValidation.isValid) {
      return versionValidation.message || 'Version validation failed';
    }

    return null;
  };


  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        if (!scanData.scanMethod) return false; // must pick a scan method first
        const versionError = getVersionValidationError();
        const hasValidVersion = !versionError && !isValidatingVersion;


        const isManual = scanData.scanMethod === 'manual';

        if (scanData.selectedApplication === 'custom') {
          const hasValidApplication = scanData.applicationName.trim() !== '' && scanData.applicationType !== '' && scanData.businessCriticality !== '';

          if (scanData.selectedProject === 'custom') {
            const isValid = hasValidApplication &&
              scanData.projectName.trim() !== '' &&
              (isManual || scanData.repourl.trim() !== '') &&
              hasValidVersion;
            return isValid;
          } else {
            const isValid = hasValidApplication &&
              scanData.selectedProject &&
              hasValidVersion;
            return isValid;
          }
        } else if (scanData.selectedProject === 'custom') {
          const isValid = scanData.selectedApplication &&
            scanData.projectName.trim() !== '' &&
            (isManual || scanData.repourl.trim() !== '') &&
            hasValidVersion;
          return isValid;
        } else {
          const isValid = scanData.selectedApplication &&
            scanData.selectedProject &&
            hasValidVersion;
          return isValid;
        }
      case 2:
        if (isLoadingSurveyQuestions || surveyQuestions.length === 0) return false;
        const unanswered = surveyQuestions.filter(question =>
          !scanData.scanparameters[question.type] || scanData.scanparameters[question.type].trim() === ''
        );
        const allQuestionsAnswered = surveyQuestions.every(question =>
          scanData.scanparameters[question.type] && scanData.scanparameters[question.type].trim() !== ''
        );
        return allQuestionsAnswered;
      case 3:
        if (scanData.scanMethod === 'manual') return true; // step 3 is skipped for manual
        if (scanData.policyType === 'default') {
          const isValid = scanData.selectedPolicy !== '';
          return isValid;
        } else {
          const isValid = Object.keys(scanData.customPolicyStandards).length > 0;
          return isValid;
        }
      case 4:
        if (scanData.scanMethod === 'manual') return !!scanData.ortUploadFile && !isUploadingOrt;
        return scanComplete;
      default:
        return true;
    }
  };

  const getSelectedPolicyDetails = () => {
    try {
      if (!showPolicyDetails) return null;

      const allPolicyList = [...systemPolicies, ...customPolicies];
      const foundPolicy = allPolicyList.find(policy => policy.id.toString() === showPolicyDetails);

      return foundPolicy;
    } catch (error) {
      return null;
    }
  };

  const getBreadcrumbInfo = () => {
    const selectedApp = applications?.find(app => app.id.toString() === scanData.selectedApplication);

    return {
      applicationName: selectedApp?.name || 'Select Application',
      projectName: scanData.selectedProject === 'custom'
        ? scanData.projectName || 'New Project'
        : scanData.projectName || 'Select Project',
      versionName: scanData.version || 'Version Required'
    };
  };

  const handleSaveCustomPolicy = async () => {
    if (scanData.customPolicyName.trim() && Object.keys(scanData.customPolicyStandards).length > 0) {
      try {
        const policyData = {
          workgroupid: workgroupId,
          name: scanData.customPolicyName,
          description: `Custom policy with ${Object.keys(scanData.customPolicyStandards).length} license rules`,
          policytype: 'Custom' as const,
          severity: 'Medium' as const,
          config: { ...scanData.customPolicyStandards },
          rulescount: Object.keys(scanData.customPolicyStandards).length
        };

        const result = await PolicyAPI.create(policyData);

        if (result.data) {
          setScanData(prev => ({
            ...prev,
            selectedPolicy: result.data!.id.toString(),
            policyType: 'default' as const,
            saveCustomPolicy: false,
            customPolicyName: '',
            customPolicyStandards: {}
          }));

          refetchPolicies();

          toast.success('Custom policy saved successfully!');
        }
      } catch (error) {
        toast.error('Failed to save custom policy. Please try again.');
      }
    }
  };

  const handleDeletePolicy = async (policyId: number, policyName: string) => {
    if (window.confirm(`Are you sure you want to delete the policy "${policyName}"? This action cannot be undone.`)) {
      try {
        await PolicyAPI.delete(policyId);

        if (scanData.selectedPolicy === policyId.toString()) {
          setScanData(prev => ({
            ...prev,
            selectedPolicy: ''
          }));
        }

        refetchPolicies();

        toast.success('Policy deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete policy. Please try again.');
      }
    }
  };

  const createNewApplication = async () => {
    if (scanData.selectedApplication !== 'custom') return null;

    try {
      const applicationData: CreateApplicationData = {
        workgroupid: workgroupId,
        name: scanData.applicationName,
        description: scanData.applicationDescription,
        phase: scanData.applicationPhase,
        applicationtype: scanData.applicationType,
        businesscriticality: scanData.businessCriticality,
        isactive: true
      };

      const response = await ApplicationAPI.create(applicationData);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const createNewProject = async (applicationId) => {
    if (scanData.selectedProject !== 'custom') return null;

    try {
      const isManual = scanData.scanMethod === 'manual';
      const projectData: CreateProjectData = {
        workgroupid: workgroupId,
        applicationid: parseInt(applicationId || scanData.selectedApplication),
        name: scanData.projectName,
        description: scanData.description,
        repourl: isManual ? '' : scanData.repourl,
        repoaccesstoken: isManual ? '' : scanData.repoaccesstoken,
        useprofilegithubtoken: !isManual && scanData.githubConnectedViaProfile,
        repousername: isManual ? '' : scanData.repousername,
        repoprovider: scanData.repoprovider,
        repotype: scanData.repotype,
      };

      const response = await ProjectAPI.create(projectData);
      return response.data;
    } catch (error) {
      throw error;
    }
  };


  const saveGithubCredentialsToProject = async (githubData: any) => {
    try {

      // The token stays server-side on the profile: a new project copies it at create time
      // (useprofilegithubtoken), an existing one via updateRepoFromProfile.
      if (scanData.selectedProject === 'custom') {
        setScanData(prev => ({
          ...prev,
          repoaccesstoken: '',
          githubConnectedViaProfile: true,
          repoprovider: 'GitHub',
          repoid: githubData.repoid,
          repousername: githubData.repousername,
          logs: []
        }));

        toast.success('GitHub credentials saved! They will be applied when you start the scan.');
        return;
      }


      const selectedProject = projects?.find(p => p.id.toString() === scanData.selectedProject);
      if (!selectedProject) {
        throw new Error(`No project selected to save credentials to ${scanData.selectedProject}`);
      }

      await ProjectAPI.updateRepoFromProfile(selectedProject.id, {
        repoid: githubData.repoid,
      });

      setScanData(prev => ({
        ...prev,
        repoaccesstoken: '',
        githubConnectedViaProfile: true,
        repoprovider: 'GitHub',
        repoid: githubData.repoid,
        repousername: githubData.repousername,
        logs: []
      }));

      toast.success('GitHub credentials saved to project successfully!');

    } catch (error) {
      // console.log('Error saving GitHub credentials to project:', error);
      console.log('Error saving GitHub credentials to project');
      toast.error('Failed to save GitHub credentials to project');
      throw error;
    }
  };

  const initiateGithubOAuth = () => {
    const selectedProject = projects?.find(p => p.id.toString() === scanData.selectedProject);

    const state = btoa(JSON.stringify({
      action: 'connect_project',
      userId: user?.id,
      projectId: selectedProject?.id,
      returnTo: 'scan',
      saveToProject: true
    }));

    const popup = window.open(
      `${api.auth.getGitHubAuthUrl()}&state=${encodeURIComponent(state)}`,
      'github-oauth-connect',
      'width=600,height=700,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,status=no'
    );

    const messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'GITHUB_OAUTH_SUCCESS') {
        popup?.close();
        window.removeEventListener('message', messageListener);

        if (event.data.data && event.data.data.data) {
          const connectResult = event.data.data.data;
          const repoid = connectResult.repoid;
          const repousername = connectResult.repousername;

          if (connectResult.connected) {
            saveGithubCredentialsToProject({ repoid, repousername })
              .then(() => {
                toast.success('GitHub credentials saved to project!');
              })
              .catch((error) => {
                toast.error('Failed to save credentials to project');
              });
          } else {
            toast.error('GitHub account could not be connected');
          }
        } else {
          toast.success('GitHub authentication successful!');
        }
      } else if (event.data.type === 'GITHUB_OAUTH_ERROR') {
        popup?.close();
        toast.error(`GitHub authentication failed: ${event.data.error}`);
        window.removeEventListener('message', messageListener);
      }
    };

    window.addEventListener('message', messageListener);

    const checkPopup = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkPopup);
        window.removeEventListener('message', messageListener);
      }
    }, 1000);
  };

  const renderPolicyDetailsDialog = () => {
    try {
      const policy = getSelectedPolicyDetails();
      if (!policy) return null;

      const isDefaultPolicy = policy.isdefault;
      return (
        <Dialog open={!!showPolicyDetails} onOpenChange={() => setShowPolicyDetails(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader className="pb-4 border-b">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-xl font-semibold text-foreground">
                  {policy.name}
                </DialogTitle>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className={
                    isDefaultPolicy ? "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700" : "bg-green-50 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700"
                  }>
                    {isDefaultPolicy ? 'System' : 'Custom'}
                  </Badge>
                  {isDefaultPolicy && (
                    <Badge variant={(policy as any).severity === 'High' || (policy as any).severity === 'Critical' ? 'destructive' : 'secondary'}>
                      {(policy as any).severity} Risk
                    </Badge>
                  )}
                </div>
              </div>
            </DialogHeader>

            <div className="py-4 space-y-6">
              <p className="text-foreground">{policy.description}</p>

              <div>
                <h3 className="font-medium text-lg mb-2">License Rules</h3>
                <p className="text-foreground">This policy applies {policy.rulescount || 0} license rules optimized for {policy.name.toLowerCase()} use cases.</p>

                {policy.config && Object.keys(policy.config).length > 0 && (
                  <div className="mt-4 space-y-3">

                    <div className="grid gap-3">
                      {Object.entries(groupPolicyConfigByRisk(policy.config)).map(([riskLevel, licenses]) => {
                        const category = LICENSE_RISK_CATEGORIES[riskLevel as keyof typeof LICENSE_RISK_CATEGORIES];
                        if (!category) return null;

                        return (
                          <div key={riskLevel} className={`p-3 rounded-lg border ${category.color} dark:border-gray-700`}>
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium text-gray-900">{category.name}</h5>
                              <Badge variant="outline" className="text-xs dark:border-gray-600 dark:text-gray-700">
                                {licenses.length} license{licenses.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                            <div className="text-xs space-y-2 text-gray-600 dark:text-gray-400">
                              <div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {licenses.map(licenseId => (
                                    <Badge key={licenseId} variant="secondary" className="text-xs dark:bg-gray-700 dark:text-gray-300">
                                      {formatLicenseId(licenseId)}
                                    </Badge>
                                  ))}
                                </div>
                              </div>

                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}


              </div>
            </div>

            <div className="pt-4 border-t flex justify-end">
              <Button onClick={() => setShowPolicyDetails(null)}
                className=""
                variant="outline"
                type="button"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      );
    } catch (error) {
      return null;
    }
  };

  const renderPolicyInfoDetailsDialog = () => {

    return (
      <Dialog open={!!showPolicyInfo} onOpenChange={() => setShowPolicyInfo(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-semibold text-foreground">
                License Risk Categorization Guide
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="py-4 space-y-6">


            <div className="space-y-4">


              {Object.entries(LICENSE_RISK_CATEGORIES).map(([key, category]) => (
                <div key={key} className={`p-4 rounded-lg border ${category.color}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-lg">{category.name}</h4>
                    <Badge variant="outline" className={`${category.color} font-medium`}>
                      {category.severity.toUpperCase()}
                    </Badge>
                  </div>

                  <p className="mb-3 text-sm">{category.description}</p>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <h5 className="font-medium text-sm mb-2">Common License Examples:</h5>
                      <ul className="text-sm space-y-1">
                        {category.examples.map((example, idx) => (
                          <li key={idx} className="flex items-center">
                            <span className="w-2 h-2 bg-current rounded-full mr-2 opacity-60"></span>
                            {example}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-medium text-sm mb-2">Typical Policy Actions:</h5>
                      <ul className="text-sm space-y-1">
                        {key === 'none' && (
                          <>
                            <li>• Automatically approved</li>
                            <li>• No restrictions</li>
                            <li>• No compliance tracking needed</li>
                          </>
                        )}
                        {key === 'low' && (
                          <>
                            <li>• Generally approved</li>
                            <li>• Minimal compliance requirements</li>
                            <li>• Attribution may be required</li>
                          </>
                        )}
                        {key === 'medium' && (
                          <>
                            <li>• Requires legal review</li>
                            <li>• Conditional approval</li>
                            <li>• Compliance documentation needed</li>
                          </>
                        )}
                        {key === 'high' && (
                          <>
                            <li>• Strict approval required</li>
                            <li>• Detailed legal analysis</li>
                            <li>• May require architecture changes</li>
                          </>
                        )}
                        {key === 'critical' && (
                          <>
                            <li>• Usually blocked/prohibited</li>
                            <li>• Executive approval required</li>
                            <li>• Alternative solutions recommended</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>

          <div className="pt-4 border-t flex justify-end">
            <Button onClick={() => setShowPolicyInfo(null)}
              className=""
              variant="outline"
              type="button"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };
  return (
    <>
      {renderPolicyDetailsDialog()}
      {renderPolicyInfoDetailsDialog()}

      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b border-border pb-6">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl">
                  {existingScan ? 'Re-Scan' : 'New Scan'}
                </DialogTitle>
                <CardDescription className="mt-2">{getStepDescription()}</CardDescription>
              </div>
            </div>


            {currentStep >= 2 && (
              <div className="flex items-center space-x-2 text-sm text-muted-foreground mt-4 p-3 bg-muted rounded-lg">
                <Package className="w-4 h-4" />
                <span className="font-medium">{getBreadcrumbInfo().applicationName}</span>
                <span className="mx-1">/</span>
                <FolderOpen className="w-4 h-4" />
                <span className="font-medium">{getBreadcrumbInfo().projectName}</span>
                <span className="mx-1">/</span>
                <GitBranch className="w-4 h-4" />
                <span className="font-medium">{getBreadcrumbInfo().versionName}</span>
              </div>
            )}

            <div className="flex items-center justify-between mt-4">
              {displaySteps.map((step, idx) => (
                <div key={step} className="flex flex-col items-center">
                  <div className={`w-${step == currentStep ? '9' : '8'} h-${step == currentStep ? '9' : '8'} rounded-full flex items-center justify-center text-sm font-medium ${step <= currentStep
                    ? 'bg-primary text-white'
                    : 'bg-muted text-muted-foreground'
                    }`}>
                    {idx + 1}
                  </div>
                  <span className={`text-xs mt-1 text-center font-${step == currentStep ? 'bold' : 'medium'} text-muted-foreground`}>
                    {stepNames[idx]}
                  </span>
                  {idx < displaySteps.length - 1 && (
                    <div className={`w-16 h-0.5 mt-2 ${step < currentStep ? 'bg-primary' : 'bg-muted'
                      }`} />
                  )}
                </div>
              ))}
            </div>
          </DialogHeader>

          <CardContent className="p-6 overflow-y-auto">
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="space-y-4">

                  <div className="space-y-2">
                    <Label htmlFor="application">Select Application</Label>
                    <Select
                      value={scanData.selectedApplication}
                      onValueChange={(value) => {
                        const application = applications?.find(app => app.id.toString() === value);
                        setScanData(prev => ({
                          ...prev,
                          selectedApplication: value,
                          selectedProject: '',
                          applicationName: application?.name || '',
                          applicationDescription: application?.description || ''
                        }));
                      }}
                    >
                      <SelectTrigger className="rounded-lg border-border">
                        <SelectValue placeholder="Choose an application..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">
                          <div className="flex items-center space-x-2">
                            <Plus className="w-4 h-4" />
                            <span>Create New Application</span>
                          </div>
                        </SelectItem>
                        {applications && applications.length > 0 ? (
                          applications.map((application) => (
                            <SelectItem key={application.id} value={application.id.toString()}>
                              {application.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="none" disabled>No applications found</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      Select an existing application or choose "Create New Application" to add one.
                    </p>
                  </div>

                  {scanData.selectedApplication === 'custom' && (
                    <div className="space-y-4 p-4 border border-border rounded-lg bg-muted dark:bg-gray-800/50 dark:border-gray-600">
                      <h4 className="font-medium text-foreground dark:text-gray-100">New Application Details</h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="applicationName">Application Name *</Label>
                          <Input
                            id="applicationName"
                            placeholder="Enter application name"
                            value={scanData.applicationName}
                            onChange={(e) => updateScanData('applicationName', e.target.value)}
                            className="rounded-lg border-border"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="applicationPhase">Development Phase</Label>
                          <Select
                            value={scanData.applicationPhase}
                            onValueChange={(value) => updateScanData('applicationPhase', value)}
                          >
                            <SelectTrigger className="rounded-lg border-border">
                              <SelectValue placeholder="Select phase..." />
                            </SelectTrigger>
                            <SelectContent>
                              {DEVELOPMENT_PHASE_OPTIONS.map((phase) => (
                                <SelectItem key={phase.value} value={phase.value}>
                                  {phase.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>                          <div className="space-y-2">
                          <Label htmlFor="applicationType">Application Type *</Label>
                          <Select
                            value={scanData.applicationType}
                            onValueChange={(value) => updateScanData('applicationType', value)}
                          >
                            <SelectTrigger className="rounded-lg border-border">
                              <SelectValue placeholder="Select type..." />
                            </SelectTrigger>
                            <SelectContent>
                              {APPLICATION_TYPE_OPTIONS.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>                        <div className="space-y-2">
                          <Label htmlFor="businessCriticality">Business Criticality *</Label>
                          <Select
                            value={scanData.businessCriticality}
                            onValueChange={(value) => updateScanData('businessCriticality', value)}
                          >
                            <SelectTrigger className="rounded-lg border-border">
                              <SelectValue placeholder="Select criticality..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="applicationDescription">Description</Label>
                        <Textarea
                          id="applicationDescription"
                          placeholder="Describe the application's purpose and functionality..."
                          value={scanData.applicationDescription}
                          onChange={(e) => updateScanData('applicationDescription', e.target.value)}
                          className="rounded-lg border-border"
                          rows={3}
                        />
                      </div>
                    </div>
                  )}

                  {scanData.selectedApplication && (
                    <div className="space-y-2">
                      <Label htmlFor="project">Select Project</Label>
                      <Select
                        value={scanData.selectedProject}
                        onValueChange={(value) => {
                          const project = projects?.find(p => p.id.toString() === value);

                          setScanData(prev => ({
                            ...prev,
                            selectedProject: value,
                            version: '',
                            projectName: project?.name || '',
                            repourl: project?.repourl || '',
                            // No override -- the project's own token (if any) is applied server-side by default.
                            repoaccesstoken: '',
                            repousername: project?.repousername || '',
                            githubConnectedViaProfile: false,
                          }));
                        }}
                      >
                        <SelectTrigger className="rounded-lg border-border">
                          <SelectValue placeholder="Choose a project..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">
                            <div className="flex items-center space-x-2">
                              <Plus className="w-4 h-4" />
                              <span>Enter New Project Details</span>
                            </div>
                          </SelectItem>
                          {scanData.selectedApplication !== 'custom' && projects && projects.length > 0 ? (
                            projects
                              .filter(project => project.applicationid?.toString() === scanData.selectedApplication)
                              .map((project) => (
                                <SelectItem key={project.id} value={project.id.toString()}>
                                  {project.name}
                                </SelectItem>
                              ))
                          ) : (
                            scanData.selectedApplication !== 'custom' && <SelectItem value="none" disabled>No projects found for this application</SelectItem>
                          )}

                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground">
                        {scanData.selectedApplication === 'custom'
                          ? 'Create a new project for your new application'
                          : 'Select an existing project from this application or choose "Enter New Project Details" to add a new one'
                        }
                      </p>
                    </div>
                  )}

                  {scanData.selectedProject === 'custom' && (
                    <div className="space-y-4 p-4 border border-border rounded-lg bg-muted dark:bg-gray-800/50 dark:border-gray-600">
                      <h4 className="font-medium text-foreground dark:text-gray-100">New Project Details</h4>

                      <div className="space-y-4">

                        <div className="space-y-2">
                          <Label htmlFor="projectName">Project Name *</Label>
                          <Input
                            id="projectName"
                            type="text"
                            value={scanData.projectName}
                            onChange={(e) => updateProjectData('projectName', e.target.value)}
                            placeholder="Enter project name"
                            className={validationErrors.projectName ? 'border-destructive' : ''}
                            maxLength={100}
                            required
                          />
                          {validationErrors.projectName && (
                            <p className="text-sm text-red-600 dark:text-red-400">{validationErrors.projectName}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="description">Description</Label>
                          <Textarea
                            id="description"
                            value={scanData.description}
                            onChange={(e) => updateProjectData('description', e.target.value)}
                            placeholder="Describe your project"
                            className="min-h-[100px]"
                          />
                        </div>


                      </div>

                      {!existingScan && (
                        <div className="space-y-2">
                          <Label>Scan Method</Label>
                          <p className="text-sm text-muted-foreground mb-2">Choose how you want to provide scan data</p>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setScanData(prev => ({ ...prev, scanMethod: 'github' }))}
                              className={`flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                                scanData.scanMethod === 'github'
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/50 hover:bg-muted/50'
                              }`}
                            >
                              <Github className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                              <div>
                                <p className="font-medium text-sm">Connect GitHub Repository</p>
                                <p className="text-xs text-muted-foreground mt-0.5">Run ORT directly on your repo via GitHub Actions</p>
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={() => setScanData(prev => ({ ...prev, scanMethod: 'manual' }))}
                              className={`flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                                scanData.scanMethod === 'manual'
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/50 hover:bg-muted/50'
                              }`}
                            >
                              <Upload className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                              <div>
                                <p className="font-medium text-sm">Manual ORT Upload</p>
                                <p className="text-xs text-muted-foreground mt-0.5">Upload an ORT result ZIP, CycloneDX JSON or SPDX JSON/YAML file you've already generated</p>
                              </div>
                            </button>
                          </div>
                        </div>
                      )}

                      {scanData.scanMethod === 'github' && <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-foreground dark:text-gray-100 flex items-center gap-2">
                          Repository Configuration
                        </h3>

                        <div className="grid grid-cols-1 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="repoprovider">Provider</Label>
                            <Select value={scanData.repoprovider} onValueChange={(value) => updateProjectData('repoprovider', value)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {REPO_PROVIDERS.map(p => (
                                  <SelectItem key={p.value} value={p.value}>
                                    <div className="flex items-center gap-2">
                                      {p.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="repourl">Repository URL*</Label>
                          <Input
                            id="repourl"
                            type="url"
                            value={scanData.repourl}
                            onChange={(e) => updateProjectData('repourl', e.target.value)}
                            placeholder="https://github.com/username/repository"
                            className={validationErrors.repourl ? 'border-destructive' : ''}
                            maxLength={500}
                          />
                          {validationErrors.repourl && (
                            <p className="text-sm text-red-600 dark:text-red-400">{validationErrors.repourl}</p>
                          )}
                        </div>


                        {/*
                        {scanData.repotype === 'private' && (
                          <div className="space-y-2">
                            <Label htmlFor="repoaccesstoken">Access Token</Label>
                            <Input
                              id="repoaccesstoken"
                              type="password"
                              value={scanData.repoaccesstoken}
                              onChange={(e) => updateProjectData('repoaccesstoken', e.target.value)}
                              placeholder="Enter access token for private repository"
                              maxLength={500}
                            />
                            <p className="text-sm text-muted-foreground">
                              Required for accessing private repositories
                            </p>
                          </div>
                        )} */}
                      </div>}

                      <div>
                        <Label>Technology Stack</Label>
                        <p className="text-sm text-muted-foreground mb-3">Select technologies used in this application</p>
                        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border rounded-lg p-3 dark:border-gray-600 dark:bg-gray-800/30">
                          {TECHNOLOGY_STACKS.map((tech) => (
                            <Badge
                              key={tech.value}
                              variant={scanData.technology.includes(tech.label) ? "default" : "outline"}
                              className={`cursor-pointer transition-all ${scanData.technology.includes(tech.label)
                                ? 'bg-primary text-white dark:bg-blue-600 dark:text-white'
                                : 'hover:bg-muted dark:hover:bg-gray-700 dark:border-gray-500 dark:text-gray-300'
                                }`}
                              onClick={() => toggleArrayValue('technology', tech.label)}
                            >
                              {tech.label}
                            </Badge>
                          ))}
                        </div>
                      </div>


                    </div>
                  )}

                  {(scanData.selectedProject && scanData.selectedProject !== "custom") && !existingScan && (
                    <div className="space-y-2">
                      <Label>Scan Method</Label>
                      <p className="text-sm text-muted-foreground mb-2">Choose how you want to provide scan data</p>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setScanData(prev => ({ ...prev, scanMethod: 'github' }))}
                          className={`flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                            scanData.scanMethod === 'github'
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50 hover:bg-muted/50'
                          }`}
                        >
                          <Github className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                          <div>
                            <p className="font-medium text-sm">Connect GitHub Repository</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Run ORT directly on your repo via GitHub Actions</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setScanData(prev => ({ ...prev, scanMethod: 'manual' }))}
                          className={`flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                            scanData.scanMethod === 'manual'
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50 hover:bg-muted/50'
                          }`}
                        >
                          <Upload className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                          <div>
                            <p className="font-medium text-sm">Manual ORT Upload</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Upload an ORT result ZIP, CycloneDX JSON or SPDX JSON/YAML file you've already generated</p>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {(scanData.selectedProject && scanData.selectedProject !== "custom") && scanData.scanMethod === 'github' &&
                    (
                      <div className="space-y-2">
                        <Label htmlFor="repourl">Repository URL*</Label>
                        <Input
                          id="repourl"
                          type="url"
                          value={scanData.repourl}
                          onChange={(e) => updateProjectData('repourl', e.target.value)}
                          placeholder="https://github.com/username/repository"
                          className={validationErrors.repourl ? 'border-destructive' : ''}
                          maxLength={500}
                        />
                        {validationErrors.repourl && (
                          <p className="text-sm text-red-600 dark:text-red-400">{validationErrors.repourl}</p>
                        )}
                      </div>
                    )
                  }
                  {scanData.selectedProject && (

                    <div className="space-y-2">
                      <Label htmlFor="version">Version *</Label>
                      <Input
                        id="version"
                        type="text"
                        placeholder="e.g., 1.0.0, v2.1.3"
                        value={scanData.version}
                        onChange={async (e) => {
                          const newVersion = e.target.value;
                          setScanData(prev => ({ ...prev, version: newVersion }));

                          if (validationErrors.version) {
                            setValidationErrors(prev => ({
                              ...prev,
                              version: ""
                            }));
                          }

                          if (newVersion.trim() && scanData.selectedProject) {
                            setTimeout(() => {
                              checkForConflictingScans(scanData.selectedProject, newVersion);
                            }, 500);
                          }
                        }}
                        className={getVersionValidationError() ? 'border-destructive' : ''}
                        maxLength={50}
                        required
                      />

                      {isValidatingVersion && (
                        <p className="text-sm text-blue-600">Validating version...</p>
                      )}
                      {getVersionValidationError() && !isValidatingVersion && (
                        <p className="text-sm text-red-600">{getVersionValidationError()}</p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        Enter a unique version number to scan. Each version must be unique within the project.
                      </p>
                    </div>
                  )}


                </div>

                <div className="bg-blue-50 p-4 rounded-lg dark:bg-blue-900/30">
                  <h4 className="font-medium text-blue-900 mb-2 dark:text-blue-200">What we'll analyze:</h4>
                  <ul className="text-sm text-blue-800 space-y-1 dark:text-blue-300">
                    <li>• License identification and classification</li>
                    <li>• Copyleft and viral license detection</li>
                    <li>• Commercial license compliance</li>
                    <li>• Dependency license conflicts</li>
                    <li>• License compatibility assessment</li>
                  </ul>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">

                {isLoadingSurveyQuestions && (
                  <div className="text-sm text-muted-foreground">Loading questions...</div>
                )}

                {surveyQuestions.map((question, questionIndex) => (
                  <div key={questionIndex} className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">
                        {question.question} <span className="text-red-500">*</span>
                      </Label>
                      <div className="mt-3 space-y-2">
                        {question.options.map((option) => {
                          return (
                            <label key={option.value} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted cursor-pointer">
                              <input
                                type="radio"
                                name={question.type}
                                value={option.value}
                                checked={scanData.scanparameters[question.type] === option.value}
                                onChange={(e) => setScanData(prev => ({
                                  ...prev,
                                  scanparameters: {
                                    ...prev.scanparameters,
                                    [question.type]: e.target.value
                                  }
                                }))}
                                className="text-primary focus:ring-primary"
                              />
                              <span className="text-sm">{option.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}


                <div className="bg-amber-50 p-4 rounded-lg dark:bg-amber-900/20">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0 dark:text-amber-400" />
                    <div>
                      <h4 className="font-medium text-amber-900 mb-1 dark:text-amber-100">Why we need this information</h4>
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        Understanding your project's scope and user base helps us tailor the scanning intensity and reporting to match the risk profile of your application.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                {scanData.configurationSaved && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3 dark:bg-green-900/20 dark:border-green-800">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="text-green-800 font-medium dark:text-green-100">Configuration Saved Successfully</p>
                      <p className="text-green-600 text-sm dark:text-green-200">Your scan configuration has been saved to the database.</p>
                    </div>
                  </div>
                )}


                <div className="flex space-x-4 p-1 bg-muted rounded-lg">
                  <button
                    onClick={() => setScanData(prev => ({ ...prev, policyType: 'default', selectedPolicy: '', customPolicyStandards: {} }))}
                    className={`flex-1 px-4 py-2 rounded-lg transition-all duration-200 ${scanData.policyType === 'default'
                      ? 'bg-white shadow-sm text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground'
                      }`}
                  >
                    Existing License Policies
                  </button>
                  {/*<EntitlementGate
                    module="policies"
                    action="create"
                    message="Custom license policies are available on the Professional plan and above."
                  >
                    <button
                      onClick={() => setScanData(prev => ({ ...prev, policyType: 'custom', selectedPolicy: '' }))}
                      className={`flex-1 px-4 py-2 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${scanData.policyType === 'custom'
                        ? 'bg-white shadow-sm text-primary font-medium'
                        : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                      Custom License Policy
                    </button>
                  </EntitlementGate>*/}
                </div>

                {scanData.policyType === 'default' && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Choose from our pre-configured license compliance policies or your custom saved policies.

                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-primary hover:text-primary/80 hover:bg-muted  dark:text-blue-400 dark:hover:text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPolicyInfo("1");
                        }}
                      >
                        Learn More
                      </Button>
                    </p>

                    <div className="relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10" />
                        <Input
                          type="text"
                          placeholder="Search policies by name, description, type, or severity..."
                          value={policySearchTerm}
                          onChange={(e) => setPolicySearchTerm(e.target.value || '')}
                          className="pl-10 h-11 rounded-lg border border-border focus:border-primary focus:ring-primary"
                          maxLength={100}
                        />
                      </div>
                      {policySearchTerm && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          Found {filteredSystemPolicies.length + filteredCustomPolicies.length} policies matching "{policySearchTerm}"
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-2 h-auto p-0 text-primary hover:text-primary/80"
                            onClick={() => setPolicySearchTerm('')}
                          >
                            Clear search
                          </Button>
                        </div>
                      )}
                    </div>

                    <div>
                      <h4 className="font-medium mb-3 flex items-center space-x-2">
                        <Shield className="w-4 h-4" />
                        <span>System Policies ({filteredSystemPolicies.length})</span>
                      </h4>
                      {filteredSystemPolicies.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {filteredSystemPolicies.map((policy) => (
                            <Card
                              key={policy.id}
                              className={`transition-all duration-200 ${scanData.selectedPolicy === policy.id.toString()
                                ? 'ring-2 ring-primary'
                                : 'hover:shadow-md'
                                }`}
                            >
                              <CardContent className="p-4">
                                <div
                                  className="cursor-pointer"
                                  onClick={() => setScanData(prev => ({ ...prev, selectedPolicy: policy.id.toString() }))}
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <h4 className="font-medium text-foreground">{policy.name}</h4>
                                    <div className="flex items-center space-x-2">
                                      <Badge variant="outline" className="text-xs">System</Badge>
                                      <Badge variant={policy.severity === 'High' || policy.severity === 'Critical' ? 'destructive' : 'secondary'}>
                                        {policy.severity}
                                      </Badge>
                                    </div>
                                  </div>
                                  <p className="text-sm text-muted-foreground mb-3">{policy.description}</p>
                                  <div className="text-xs text-muted-foreground">
                                    {policy.rulescount > 0 && (<Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-xs text-primary hover:text-primary/80 hover:bg-muted dark:text-blue-400 dark:hover:text-blue-600"
                                      onClick={(e) => {
                                        try {
                                          e.stopPropagation();
                                          setShowPolicyDetails(policy.id.toString());
                                        } catch (error) {
                                        }
                                      }}
                                    >
                                      {policy.rulescount || 0} license rules
                                    </Button>)}
                                  </div>
                                </div>

                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Shield className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                          <p>No system policies found matching "{policySearchTerm}"</p>
                        </div>
                      )}
                    </div>

                    {(customPolicies.length > 0 || policySearchTerm) && (
                      <div>
                        <h4 className="font-medium mb-3 flex items-center space-x-2">
                          <Settings className="w-4 h-4" />
                          <span>Custom Policies ({filteredCustomPolicies.length})</span>
                        </h4>
                        {filteredCustomPolicies.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredCustomPolicies.map((policy) => (
                              <Card
                                key={policy.id}
                                className={`transition-all duration-200 ${scanData.selectedPolicy === policy.id.toString()
                                  ? 'ring-2 ring-primary'
                                  : 'hover:shadow-md'
                                  }`}
                              >
                                <CardContent className="p-4">
                                  <div
                                    className="cursor-pointer"
                                    onClick={() => setScanData(prev => ({ ...prev, selectedPolicy: policy.id.toString() }))}
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <h4 className="font-medium text-foreground">{policy.name}</h4>
                                      <div className="flex items-center space-x-2">
                                        <Badge variant="outline" className="text-xs bg-green-50 text-green-800 border-green-200">
                                          Custom
                                        </Badge>
                                        {canDeletePolicy && (
                                          <EntitlementGate
                                            module="policies"
                                            action="delete"
                                            message="Deleting custom policies is available on the Professional plan and above."
                                          >
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="p-1 h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeletePolicy(policy.id, policy.name);
                                              }}
                                              title="Delete Policy"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </Button>
                                          </EntitlementGate>
                                        )}
                                      </div>
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-3">{policy.description}</p>
                                    <div className="text-xs text-muted-foreground">
                                      {policy.rulescount > 0 && (<Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-xs text-primary hover:text-primary/80 hover:bg-muted dark:text-blue-400 dark:hover:text-blue-600"
                                        onClick={(e) => {
                                          try {
                                            e.stopPropagation();
                                            setShowPolicyDetails(policy.id.toString());
                                          } catch (error) {
                                          }
                                        }}
                                      >
                                        {policy.rulescount || 0} license rules
                                      </Button>)}


                                    </div>
                                  </div>


                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <Settings className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                            <p>
                              {policySearchTerm
                                ? `No custom policies found matching "${policySearchTerm}"`
                                : "No custom policies created yet"}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {scanData.policyType === 'custom' && (
                  <div className="space-y-6">
                    <div className="bg-amber-50 p-4 rounded-lg dark:bg-amber-900/20">
                      <div className="flex items-start space-x-2">
                        <Settings className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0 dark:text-amber-400" />
                        <div>
                          <h4 className="font-medium text-amber-900 mb-1 dark:text-amber-100">Custom License Policy Configuration</h4>
                          <p className="text-sm text-amber-800 dark:text-amber-200">
                            Select license types and assign risk severity levels. Only licenses with assigned severity levels will be included in your compliance scan.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium">Configure License Risk Assessment</h4>

                      {['Critical Risk', 'High Risk', 'Medium Risk', 'Low Risk', 'Commercial', 'Unknown Risk'].map((category) => {
                        const categoryLicenses = POLICY_STANDARDS.filter(license => license.category === category);
                        if (categoryLicenses.length === 0) return null;

                        return (
                          <div key={category} className="space-y-3">
                            <h5 className="text-sm font-medium text-foreground border-b border-border pb-1">
                              {category}
                            </h5>
                            <div className="grid grid-cols-1 gap-3">
                              {categoryLicenses.map((license) => (
                                <div key={license.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                                  <div>
                                    <h6 className="font-medium text-sm">{license.name}</h6>
                                    <p className="text-xs text-muted-foreground">{license.category}</p>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div className="flex space-x-1">
                                      {SEVERITY_LEVELS.map((severity) => (
                                        <button
                                          key={severity.id}
                                          onClick={() => {
                                            const newStandards = { ...scanData.customPolicyStandards };
                                            if (newStandards[license.id] === severity.id) {
                                              delete newStandards[license.id];
                                            } else {
                                              newStandards[license.id] = severity.id;
                                            }
                                            setScanData(prev => ({ ...prev, customPolicyStandards: newStandards }));
                                          }}
                                          className={`px-2 py-1 text-xs rounded-md border transition-all duration-200 ${scanData.customPolicyStandards[license.id] === severity.id
                                            ? severity.color + ' font-medium'
                                            : 'bg-muted text-muted-foreground border-border hover:bg-muted'
                                            }`}
                                        >
                                          {severity.name}
                                        </button>
                                      ))}
                                    </div>
                                    {scanData.customPolicyStandards[license.id] && (
                                      <button
                                        onClick={() => {
                                          const newStandards = { ...scanData.customPolicyStandards };
                                          delete newStandards[license.id];
                                          setScanData(prev => ({ ...prev, customPolicyStandards: newStandards }));
                                        }}
                                        className="text-muted-foreground hover:text-red-500 transition-colors"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {Object.keys(scanData.customPolicyStandards).length > 0 && (
                      <div className="space-y-4">
                        <div className="bg-blue-50 p-4 rounded-lg dark:bg-blue-900/30">
                          <h4 className="font-medium text-blue-900 mb-3 dark:text-blue-200">
                            Selected License Types ({Object.keys(scanData.customPolicyStandards).length})
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(scanData.customPolicyStandards).map(([licenseId, severityId]) => {
                              const license = POLICY_STANDARDS.find(s => s.id === licenseId);
                              const severity = SEVERITY_LEVELS.find(s => s.id === severityId);
                              if (!license || !severity) return null;

                              return (
                                <div key={licenseId} className="flex items-center space-x-1 bg-white px-2 py-1 rounded-md border dark:bg-muted dark:border-border">
                                  <span className="text-xs font-medium">{license.name}</span>
                                  <span className={`text-xs px-1 py-0.5 rounded ${severity.color}`}>
                                    {severity.name}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {canCreatePolicy && (
                          <div className="bg-green-50 p-4 rounded-lg dark:bg-green-900/30">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-green-900 dark:text-green-200">Save Custom Policy</h4>
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={scanData.saveCustomPolicy}
                                  onChange={(e) => setScanData(prev => ({ ...prev, saveCustomPolicy: e.target.checked }))}
                                  className="text-primary focus:ring-primary"
                                />
                                <span className="text-sm text-green-800 dark:text-green-300">Save for future use</span>
                              </label>
                            </div>

                            {scanData.saveCustomPolicy && (
                              <div className="space-y-3">
                                <Input
                                  placeholder="Enter policy name"
                                  value={scanData.customPolicyName}
                                  onChange={(e) => setScanData(prev => ({ ...prev, customPolicyName: e.target.value }))}
                                  className="bg-white border-green-200 focus:border-green-400 dark:bg-background dark:border-green-700 dark:focus:border-green-500"
                                  maxLength={100}
                                />
                                <EntitlementGate
                                  module="policies"
                                  action="create"
                                  message="Saving custom policies for reuse is available on the Professional plan and above."
                                >
                                  <Button
                                    onClick={handleSaveCustomPolicy}
                                    disabled={!scanData.customPolicyName.trim()}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                                  >
                                    Save Custom Policy
                                  </Button>
                                </EntitlementGate>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {currentStep === 4 && scanData.scanMethod === 'manual' && (
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-1">Upload ORT Result File</h4>
                  <p className="text-sm text-muted-foreground">
                    Upload a ZIP file generated by ORT. It must contain{' '}
                    <code className="px-1 py-0.5 bg-muted rounded text-xs">evaluation-result.json</code>{' '}
                    with <code className="px-1 py-0.5 bg-muted rounded text-xs">analyzer</code>,{' '}
                    <code className="px-1 py-0.5 bg-muted rounded text-xs">advisor</code>, and{' '}
                    <code className="px-1 py-0.5 bg-muted rounded text-xs">evaluator</code> sections —
                    or a standalone CycloneDX SBOM <code className="px-1 py-0.5 bg-muted rounded text-xs">.json</code> file,
                    or an SPDX document (<code className="px-1 py-0.5 bg-muted rounded text-xs">.json</code> /{' '}
                    <code className="px-1 py-0.5 bg-muted rounded text-xs">.yml</code>).
                  </p>
                </div>

                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    if (isUploadingOrt) return;
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      if (!isAcceptedScanResultFile(file.name)) { toast.error('Only .zip, .json, .yml or .yaml files are accepted'); return; }
                      if (file.size > 100 * 1024 * 1024) { toast.error('File must be under 100MB'); return; }
                      setOrtValidationError(null);
                      setScanData(prev => ({ ...prev, ortUploadFile: file }));
                    }
                  }}
                  onClick={() => { if (!isUploadingOrt) (document.getElementById('ort-file-input') as HTMLInputElement)?.click(); }}
                  className={`border-2 border-dashed border-border rounded-lg p-10 text-center transition-colors ${isUploadingOrt ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}`}
                >
                  <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  {scanData.ortUploadFile ? (
                    <div>
                      <p className="font-medium">{scanData.ortUploadFile.name}</p>
                      <p className="text-sm text-muted-foreground mt-1">{(scanData.ortUploadFile.size / 1024 / 1024).toFixed(2)} MB — click to change</p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium">Drag & drop ORT ZIP, CycloneDX JSON or SPDX JSON/YAML here</p>
                      <p className="text-sm text-muted-foreground mt-1">or click to browse — .zip, .json, .yml or .yaml, max 100MB</p>
                    </div>
                  )}
                  <input
                    id="ort-file-input"
                    type="file"
                    accept={SCAN_RESULT_ACCEPT}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 100 * 1024 * 1024) { toast.error('File must be under 100MB'); return; }
                        setOrtValidationError(null);
                        setScanData(prev => ({ ...prev, ortUploadFile: file }));
                      }
                    }}
                  />
                </div>

                {ortValidationError && (
                  <div className="flex items-start gap-2 text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 rounded-lg">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <p className="text-sm">{ortValidationError}</p>
                  </div>
                )}

                {(isUploadingOrt || manualUploadLogs.length > 0) && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {manualUploadComplete ? 'Processing complete' : isUploadingOrt ? 'Processing...' : 'Upload status'}
                        </span>
                        <span>{manualUploadProgress}%</span>
                      </div>
                      <Progress value={manualUploadProgress} className="h-2" />
                    </div>

                    <div className="bg-slate-900 dark:bg-slate-950 text-green-400 p-4 rounded-lg font-mono text-sm max-h-52 overflow-y-auto">
                      {manualUploadLogs.length === 0 ? (
                        <div className="text-slate-500">Waiting for logs...</div>
                      ) : (
                        manualUploadLogs.map((log, i) => (
                          <div key={i} className="mb-1">
                            <span className="text-slate-500">[{new Date(log.timestamp).toLocaleTimeString()}]</span>{' '}
                            {log.message}
                          </div>
                        ))
                      )}
                      <div ref={manualUploadLogsEndRef} />
                    </div>
                  </div>
                )}

                {!isUploadingOrt && !manualUploadLogs.length && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2 text-sm">Expected ZIP structure:</h4>
                    <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1 font-mono">
                      <li>📦 your-ort-result.zip</li>
                      <li>&nbsp;&nbsp;└── evaluation-result.json</li>
                      <li>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;├── analyzer.result.packages</li>
                      <li>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;├── advisor.results</li>
                      <li>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└── evaluator.findings</li>
                    </ul>
                    <p className="text-xs text-blue-800 dark:text-blue-300 mt-2">
                      Or upload a standalone <span className="font-mono">bom.json</span> CycloneDX SBOM
                      (must have <span className="font-mono">"bomFormat": "CycloneDX"</span>) — license issues won't
                      be populated for CycloneDX-sourced scans since it has no equivalent of ORT's policy violations.
                    </p>
                    <p className="text-xs text-blue-800 dark:text-blue-300 mt-2">
                      Or upload an SPDX 2.x document as <span className="font-mono">.json</span> or{' '}
                      <span className="font-mono">.yml</span> (must have <span className="font-mono">"spdxVersion": "SPDX-2.x"</span>) —
                      license issues and vulnerabilities won't be populated for SPDX-sourced scans, since SPDX 2.x carries
                      neither; packages the document describes are treated as the scanned project, not as components.
                    </p>
                  </div>
                )}

                {manualUploadComplete && scanResults && (
                  <div className="space-y-6 pt-2 border-t">
                    <div>
                      <h4 className="font-medium mb-3">Scan Summary</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        <Card className="bg-blue-50/10 border-blue-200/50 dark:bg-blue-500/10 dark:border-blue-400/20">
                          <CardContent className="p-4 text-center">
                            <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{scanResults.summary.totalComponents.toLocaleString()}</div>
                            <div className="text-xs text-blue-700 dark:text-blue-300">Total Components</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-cyan-50/10 border-cyan-200/50 dark:bg-cyan-500/10 dark:border-cyan-400/20">
                          <CardContent className="p-4 text-center">
                            <div className="text-xl font-bold text-cyan-600 dark:text-cyan-400">{scanResults.summary.totalEncryptionComponents.toLocaleString()}</div>
                            <div className="text-xs text-cyan-700 dark:text-cyan-300">Encryption Algorithms</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-amber-50/10 border-amber-200/50 dark:bg-amber-500/10 dark:border-amber-400/20">
                          <CardContent className="p-4 text-center">
                            <div className="text-xl font-bold text-amber-600 dark:text-amber-400">{scanResults.summary.totalLicenseIssues.toLocaleString()}</div>
                            <div className="text-xs text-amber-700 dark:text-amber-300">License Issues</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-red-50/10 border-red-200/50 dark:bg-red-500/10 dark:border-red-400/20">
                          <CardContent className="p-4 text-center">
                            <div className="text-xl font-bold text-red-600 dark:text-red-400">{scanResults.summary.vulnerabilitiesFound.toLocaleString()}</div>
                            <div className="text-xs text-red-700 dark:text-red-300">Vulnerabilities Found</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-green-50/10 border-green-200/50 dark:bg-green-500/10 dark:border-green-400/20">
                          <CardContent className="p-4 text-center">
                            <div className="text-xl font-bold text-green-600 dark:text-green-400">{scanResults.summary.provenance.toLocaleString()}</div>
                            <div className="text-xs text-green-700 dark:text-green-300">Provenance</div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium mb-3">Risk Assessment Scores</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        <Card className="bg-red-50/10 border-red-200/50 dark:bg-red-500/10 dark:border-red-400/20">
                          <CardContent className="p-4 text-center">
                            <div className="text-xl font-bold text-red-600 dark:text-red-400">{Number(scanResults.riskScores.overallRiskScore).toFixed(2)}</div>
                            <div className="text-xs text-red-700 dark:text-red-300">Overall Risk Score</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-purple-50/10 border-purple-200/50 dark:bg-purple-500/10 dark:border-purple-400/20">
                          <CardContent className="p-4 text-center">
                            <div className="text-xl font-bold text-purple-600 dark:text-purple-400">{Number(scanResults.riskScores.totalTechnicalDebt).toFixed(2)}</div>
                            <div className="text-xs text-purple-700 dark:text-purple-300">Technical Debt</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-indigo-50/10 border-indigo-200/50 dark:bg-indigo-500/10 dark:border-indigo-400/20">
                          <CardContent className="p-4 text-center">
                            <div className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{Number(scanResults.riskScores.operationalRiskScore).toFixed(2)}</div>
                            <div className="text-xs text-indigo-700 dark:text-indigo-300">Operational Risk</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-orange-50/10 border-orange-200/50 dark:bg-orange-500/10 dark:border-orange-400/20">
                          <CardContent className="p-4 text-center">
                            <div className="text-xl font-bold text-orange-600 dark:text-orange-400">{Number(scanResults.riskScores.vulnerabilityRiskScore).toFixed(2)}</div>
                            <div className="text-xs text-orange-700 dark:text-orange-300">Vulnerability Risk</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-yellow-50/10 border-yellow-200/50 dark:bg-yellow-500/10 dark:border-yellow-400/20">
                          <CardContent className="p-4 text-center">
                            <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{Number(scanResults.riskScores.licenseRiskScore).toFixed(2)}</div>
                            <div className="text-xs text-yellow-700 dark:text-yellow-300">License Risk</div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-medium">Available Reports</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium">ORT Report</h5>
                              <Badge variant="outline">Multiple Formats</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">Detailed OSS Review Toolkit report with vulnerability details</p>
                            <Button variant="outline" className="w-full rounded-lg" onClick={async () => {
                              if (scanData.scanId) {
                                setIsDownloading(true);
                                try { await ScanAPI.downloadOrtReport(scanData.scanId); toast.success('ORT report downloaded'); }
                                catch { toast.error('Failed to download ORT report'); }
                                finally { setIsDownloading(false); }
                              }
                            }} disabled={!scanData.scanId || isDownloading}>
                              <Download className="w-4 h-4 mr-2" />
                              {isDownloading ? 'Downloading...' : 'Download ORT Report'}
                            </Button>
                          </CardContent>
                        </Card>
                        <Card className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium">SCC Report</h5>
                              <Badge variant="outline">Interactive</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">Interactive Source Code Control dashboard with detailed analysis</p>
                            <Button variant="outline" className="w-full rounded-lg" onClick={() => navigate('/component-view-report/' + scanData.scanId)}>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              View SCC Report
                            </Button>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {currentStep === 4 && scanData.scanMethod === 'github' && (
              <div className="space-y-6">

                {((scanData.repoaccesstoken || scanData.githubConnectedViaProfile || (scanData.selectedProject !== 'custom' && projects?.find(p => p.id.toString() === scanData.selectedProject)?.hasrepoaccesstoken)) && scanData.repousername) ? (
                  <>
                    <div className="space-y-4">


                      {false && (
                        <div className="space-y-4 p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                          <h4 className="font-medium text-blue-900 flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            ORT Configuration
                          </h4>

                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label>ORT Analysis Type</Label>
                              <Select
                                value={scanData.ortScanType}
                                onValueChange={(value) => {
                                  const ortConfig = getORTConfigTemplate(value);
                                  setScanData(prev => ({
                                    ...prev,
                                    ortScanType: value,
                                    ortPackageManagers: ortConfig.enabledPackageManagers,
                                    ortRunStages: ortConfig.runStages
                                  }));
                                }}
                              >
                                <SelectTrigger className="bg-card border-border">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ORT_SCAN_TYPES.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label>Package Managers</Label>
                              <p className="text-sm text-muted-foreground mb-2">Select package managers to analyze</p>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {ORT_PACKAGE_MANAGERS.map((pm) => (
                                  <label key={pm.value} className="flex items-center space-x-2 p-2 border rounded bg-white cursor-pointer hover:bg-muted">
                                    <input
                                      type="checkbox"
                                      checked={scanData.ortPackageManagers.includes(pm.value)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setScanData(prev => ({
                                            ...prev,
                                            ortPackageManagers: [...prev.ortPackageManagers, pm.value]
                                          }));
                                        } else {
                                          setScanData(prev => ({
                                            ...prev,
                                            ortPackageManagers: prev.ortPackageManagers.filter(p => p !== pm.value)
                                          }));
                                        }
                                      }}
                                      className="text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm">{pm.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label>Fail Scan On</Label>
                              <Select
                                value={scanData.ortFailOn}
                                onValueChange={(value) => setScanData(prev => ({ ...prev, ortFailOn: value }))}
                              >
                                <SelectTrigger className="bg-card border-border">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ORT_FAIL_ON_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label>Analysis Stages</Label>
                              <p className="text-sm text-muted-foreground mb-2">Select ORT analysis stages to run</p>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {ORT_RUN_STAGES.map((stage) => (
                                  <label key={stage.value} className="flex items-center space-x-2 p-2 border rounded bg-white cursor-pointer hover:bg-muted">
                                    <input
                                      type="checkbox"
                                      checked={scanData.ortRunStages.includes(stage.value)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setScanData(prev => ({
                                            ...prev,
                                            ortRunStages: [...prev.ortRunStages, stage.value]
                                          }));
                                        } else {
                                          setScanData(prev => ({
                                            ...prev,
                                            ortRunStages: prev.ortRunStages.filter(s => s !== stage.value)
                                          }));
                                        }
                                      }}
                                      className="text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm">{stage.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between">

                      {!isScanning && !scanComplete && !scanCancelled && (
                        <Button
                          onClick={startScan}
                          className="bg-primary hover:bg-primary/80 text-white rounded-lg"
                        >
                          Start Scan
                        </Button>
                      )}
                      {isScanning && !scanComplete && (
                        <Button
                          onClick={cancelScan}
                          disabled={isCancelling}
                          variant="destructive"
                          className="rounded-lg"
                        >
                          {isCancelling ? 'Cancelling...' : 'Cancel Scan'}
                        </Button>
                      )}
                      {scanCancelled && (
                        <Button
                          onClick={() => {
                            setScanCancelled(false);
                            setScanData(prev => ({ ...prev, scanProgress: 0, logs: [] }));
                          }}
                          className="bg-primary hover:bg-primary/80 text-white rounded-lg"
                        >
                          Start New Scan
                        </Button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Progress</span>
                        <span>{scanData.scanProgress}%</span>
                      </div>
                      <Progress value={scanData.scanProgress} className="h-3" />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {isScanning && !isCancelling && <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />}
                        {isCancelling && <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />}
                        {scanComplete && <CheckCircle className="w-4 h-4 text-green-500" />}
                        {scanCancelled && <XIcon className="w-4 h-4 text-orange-500" />}
                        <span className="text-sm font-medium">
                          {isCancelling ? 'Cancelling scan...' :
                            isScanning ? 'Scanning in progress...' :
                              scanCancelled ? 'Scan cancelled' :
                                scanComplete ? 'Scan completed!' : 'Ready to start'}
                        </span>
                      </div>


                    </div>

                    <div className="bg-slate-900 dark:bg-slate-950 text-green-400 p-4 rounded-lg font-mono text-sm max-h-60 overflow-y-auto">
                      {scanData.logs.length === 0 ? (
                        <div className="text-slate-500 dark:text-slate-400">Scan logs will appear here...</div>
                      ) : (
                        scanData.logs.map((log, index) => (
                          <div key={index} className="mb-1">
                            <span className="text-slate-500 dark:text-slate-400">[{new Date(log.timestamp).toLocaleTimeString()}]</span> {log.message}
                          </div>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {(() => {

                      return (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center dark:bg-amber-900/20 dark:border-amber-800">
                          <div className="flex flex-col items-center space-y-4">
                            <Github className="w-12 h-12 text-amber-600 dark:text-amber-400" />
                            <div>
                              <h3 className="text-lg font-semibold text-amber-900 mb-2 dark:text-amber-100">GitHub Authentication Required</h3>
                              <p className="text-amber-800 mb-4 dark:text-amber-200">
                                This project uses a private repository. Please authenticate with GitHub to provide access credentials for scanning.
                              </p>
                            </div>
                            <Button
                              onClick={() => initiateGithubOAuth()}
                              className="bg-[#24292e] hover:bg-[#1c2025] text-white rounded-lg flex items-center space-x-2 dark:bg-[#1c2025] dark:hover:bg-[#161b1f]"
                            >
                              <Github className="w-4 h-4" />
                              <span>Authenticate with GitHub</span>
                            </Button>
                          </div>
                        </div>
                      );
                    })()}



                  </>
                )}

              </div>
            )}

            {currentStep === 5 && (
              <div className="space-y-6">
                {scanResults ? (
                  <>
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium mb-3">Scan Summary</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                          <Card className="bg-blue-50/10 border-blue-200/50 dark:bg-blue-500/10 dark:border-blue-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                {scanResults?.summary.totalComponents.toLocaleString() || '0'}
                              </div>
                              <div className="text-xs text-blue-700 dark:text-blue-300">Total Components</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-cyan-50/10 border-cyan-200/50 dark:bg-cyan-500/10 dark:border-cyan-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-cyan-600 dark:text-cyan-400">
                                {scanResults?.summary.totalEncryptionComponents.toLocaleString() || '0'}
                              </div>
                              <div className="text-xs text-cyan-700 dark:text-cyan-300">Total Encryption Algorithms</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-amber-50/10 border-amber-200/50 dark:bg-amber-500/10 dark:border-amber-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
                                {scanResults?.summary.totalLicenseIssues.toLocaleString() || '0'}
                              </div>
                              <div className="text-xs text-amber-700 dark:text-amber-300">Total License Issues</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-red-50/10 border-red-200/50 dark:bg-red-500/10 dark:border-red-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-red-600 dark:text-red-400">
                                {scanResults?.summary.vulnerabilitiesFound.toLocaleString() || '0'}
                              </div>
                              <div className="text-xs text-red-700 dark:text-red-300">Vulnerabilities Found</div>
                            </CardContent>
                          </Card>
                          {/* <Card className="bg-rose-50/10 border-rose-200/50 dark:bg-rose-500/10 dark:border-rose-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-rose-600 dark:text-rose-400">
                                {scanResults?.summary.exploitableVulnerabilities.toLocaleString() || '0'}
                              </div>
                              <div className="text-xs text-rose-700 dark:text-rose-300">Exploitable Vulnerabilities</div>
                            </CardContent>
                          </Card> */}
                          <Card className="bg-green-50/10 border-green-200/50 dark:bg-green-500/10 dark:border-green-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-green-600 dark:text-green-400">
                                {scanResults?.summary.provenance.toLocaleString() || '0'}
                              </div>
                              <div className="text-xs text-green-700 dark:text-green-300">Provenance</div>
                            </CardContent>
                          </Card>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-3">Risk Assessment Scores</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                          {/* <Card className="bg-blue-50/10 border-blue-200/50 dark:bg-blue-500/10 dark:border-blue-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                {Number(scanResults?.riskScores.businessRisk)?.toFixed(2) || '0.0'}
                              </div>
                              <div className="text-xs text-blue-700 dark:text-blue-300">Business Risk</div>
                            </CardContent>
                          </Card> */}
                           <Card className="bg-red-50/10 border-red-200/50 dark:bg-red-500/10 dark:border-red-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-red-600 dark:text-red-400">
                                {Number(scanResults?.riskScores.overallRiskScore)?.toFixed(2) || '0.0'}
                              </div>
                              <div className="text-xs text-red-700 dark:text-red-300">Overall Risk Score</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-purple-50/10 border-purple-200/50 dark:bg-purple-500/10 dark:border-purple-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                                {Number(scanResults?.riskScores.totalTechnicalDebt)?.toFixed(2) || '0.0'}
                              </div>
                              <div className="text-xs text-purple-700 dark:text-purple-300">Total Technical Debt</div>
                            </CardContent>
                          </Card>
                         
                          <Card className="bg-indigo-50/10 border-indigo-200/50 dark:bg-indigo-500/10 dark:border-indigo-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                                {Number(scanResults?.riskScores.operationalRiskScore)?.toFixed(2) || '0.0'}
                              </div>
                              <div className="text-xs text-indigo-700 dark:text-indigo-300">Operational Risk Score</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-orange-50/10 border-orange-200/50 dark:bg-orange-500/10 dark:border-orange-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                                {Number(scanResults?.riskScores.vulnerabilityRiskScore)?.toFixed(2) || '0.0'}
                              </div>
                              <div className="text-xs text-orange-700 dark:text-orange-300">Vulnerability Risk Score</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-yellow-50/10 border-yellow-200/50 dark:bg-yellow-500/10 dark:border-yellow-400/20">
                            <CardContent className="p-4 text-center">
                              <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">
                                {Number(scanResults?.riskScores.licenseRiskScore)?.toFixed(2) || '0.0'}
                              </div>
                              <div className="text-xs text-yellow-700 dark:text-yellow-300">License Risk Score</div>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium">Available Reports</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium">ORT Report</h5>
                              <Badge variant="outline">Multiple Formats</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                              Detailed OSS Review Toolkit report with vulnerability details
                            </p>

                            <Button
                              variant="outline"
                              className="w-full rounded-lg"
                              onClick={async () => {
                                if (scanData.scanId) {
                                  setIsDownloading(true);
                                  try {
                                    await ScanAPI.downloadOrtReport(scanData.scanId);
                                    toast.success('ORT report downloaded successfully');
                                  } catch (error) {
                                    toast.error('Failed to download ORT report. Please try again.');
                                  } finally {
                                    setIsDownloading(false);
                                  }
                                }
                              }}
                              disabled={!scanData.scanId || !scanComplete || isDownloading}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              {isDownloading ? 'Downloading...' : 'Download ORT Report'}
                            </Button>
                          </CardContent>
                        </Card>

                        <Card className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium">SCC Report</h5>
                              <Badge variant="outline">Interactive</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                              Interactive Source Code Control dashboard with detailed analysis
                            </p>
                            <Button
                              variant="outline"
                              className="w-full rounded-lg"
                              onClick={() => navigate('/component-view-report/' + scanData.scanId)}
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              View SCC Report
                            </Button>

                          </CardContent>
                        </Card>

                        {scanData.scanId && scanProgress[scanData.scanId] && scanProgress[scanData.scanId].workflowUrl && (
                          <Card className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="font-medium">GitHub Actions Raw Log</h5>
                                <Badge variant="outline">External</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mb-3">
                                View detailed GitHub Actions workflow execution logs
                              </p>
                              <Button
                                variant="outline"
                                className="w-full rounded-lg"
                                onClick={() => {
                                  const workflowUrl = scanProgress[scanData.scanId!].workflowUrl;
                                  if (workflowUrl) {
                                    window.open(workflowUrl, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                              >
                                <Github className="w-4 h-4 mr-2" />
                                View GitHub Actions Log
                              </Button>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-muted-foreground mb-4">
                      Scan results will appear here once the scan is completed.
                    </div>
                    {isScanning && (
                      <div className="space-y-2">
                        <Progress value={scanData.scanProgress} className="w-full max-w-md mx-auto" />
                        <div className="text-sm text-muted-foreground space-y-1">
                          <div className="flex items-center space-x-2">
                            <span>Scan in progress: {scanData.scanProgress}%</span>

                          </div>
                          {isSubscribed && (
                            <div className="text-xs text-blue-600">
                              Real-time monitoring active
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>

          <div className="border-t border-border p-6 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === (existingScan ? 2 : 1)}
              className="rounded-lg"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            <div className="flex space-x-2">
              {(currentStep < 5 && !(currentStep === 4 && scanData.scanMethod === 'manual' && manualUploadComplete)) ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="rounded-lg"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={!isStepValid() || isSavingConfiguration || isUploadingOrt}
                    className="bg-primary hover:bg-primary/80 text-white rounded-lg"
                  >
                    {isUploadingOrt && currentStep === 4 ? (
                      <>
                        <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        Uploading...
                      </>
                    ) : isSavingConfiguration && currentStep === 2 ? (
                      <>
                        <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        Saving...
                      </>
                    ) : currentStep === 4 && scanData.scanMethod === 'manual' ? (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload & Analyze
                      </>
                    ) : (
                      <>
                        Next
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </>

              ) : (
                <Button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg"
                  variant="outline"
                >
                  Close
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
}